import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { LeftRightCoverageIcon } from "./LeftRightCoverageIcon";

import {
  ActiveRallyVideo,
  type ActiveRallyLensMoment,
} from "./ActiveRallyVideo";
import type { FirefightClip } from "./GameStory_firefights";
import type { CvJson } from "../../shared/types/cvJson";
import type {
  KitchenArrivalRow,
  ShotNumberOverlay,
} from "./RallyBoardPopup";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";

export const COACH_VIEW_THRESHOLDS = {
  candidateSpeedupMph: 32,
  candidateBodybagMph: 24,
  possiblePopupDinkHeightOverNetFt: 3.8,
  possiblePopupDropHeightOverNetFt: 5.0,
  possiblePopupOtherHeightOverNetFt: 4.0,
  bodybagOpponentDistanceFt: 3.5,
  kitchenAttackMph: 24,
  fastExchangeMs: 850,
  preContactMs: 1400,
  postContactMs: 1400,
  coveragePreContactMs: 2200,
  coveragePostContactMs: 1800,
  paddleClipPaddingMs: 250,
  speedupClipPaddingMs: 250,
  courtWidthFt: 20,
  middleLineX: 10,
  middleBallHalfWidthFt: 2.25,
  courtNetY: 22,
  kitchenHalfDepthFt: 7,
  kitchenPositionLeewayFt: 2.5,
  transitionMaxDistanceFromNetFt: 17,
} as const;

export type CoachViewMomentKind =
  | "candidateSpeedup"
  | "candidateBodybag"
  | "possiblePopup"
  | "paddleReadiness"
  | "dinkReadiness"
  | "leftSideCoverage";

export interface CoachViewMoment {
  kind: CoachViewMomentKind;
  key: string;
  gameId: string;
  rallyId: string;
  rallyIndex: number;
  shotId: string;
  shotIndex: number;
  shotNumber: number;
  reviewedPlayerId: number;
  hitterPlayerId: number;
  partnerPlayerId?: number;
  hitterTeam: 0 | 1 | null;
  reviewedTeam: 0 | 1 | null;
  shotType?: string;
  rallyWinnerTeamId?: 0 | 1 | null;
  servingTeam?: 0 | 1 | null;
  receivingTeam?: 0 | 1 | null;
  score?: string;
  gameStageKey: string;
  gameStageLabel: string;
  reviewedPlayerZone: "kitchen" | "transition" | "deep" | "unknown";
  hitterPlayerZone: "kitchen" | "transition" | "deep" | "unknown";
  incomingBounceKind?: "volley_no_bounce" | "after_bounce" | "unknown";
  speedupLane?: "middle" | "down_line" | "crosscourt" | "unknown";
  coverageCandidateKind?:
    | "middle_take"
    | "partner_side_take"
    | "body_cross"
    | "partner_middle_ball";
  contactLocationLabel?: string;
  coverageOutcome?: "left_side_took" | "left_side_did_not_take";
  coveragePartnerSideDistanceFt?: number | null;
  contactTimestamp: number;
  contactFrame?: number;
  candidateReason?: string;
  candidateScore?: number;
  possiblePopupCourtState?: "all_kitchen" | "transitioning" | "unknown";
  thumbnailUrl?: string;
  clipStartMs: number;
  clipEndMs: number;
}

type TeamResolver = (playerId: number | null) => 0 | 1 | null;

function lower(value: unknown): string {
  return String(value ?? "").toLowerCase();
}

function displayShotType(overlay: ShotNumberOverlay): string {
  return (
    overlay.rawShot?.shot_type ??
    overlay.shotType ??
    overlay.featuresLabel ??
    overlay.label ??
    "unknown"
  );
}

function normalizedText(value: unknown): string {
  return lower(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
}

function overlayTextBag(overlay: ShotNumberOverlay): string {
  const rawShot = overlay.rawShot as any;
  const pieces = [
    rawShot?.shot_type,
    rawShot?.stroke_type,
    rawShot?.winner_type,
    rawShot?.end_zone,
    rawShot?.result,
    rawShot?.outcome,
    rawShot?.shot_result,
    rawShot?.label,
    overlay.shotType,
    overlay.label,
    overlay.featuresLabel,
    ...(overlay.storyKinds ?? []),
    ...Object.keys(rawShot?.tags ?? {}),
  ];
  return normalizedText(pieces.filter(Boolean).join(" "));
}

function derivedKitchenShotType(overlay: ShotNumberOverlay): string {
  const rawShot = overlay.rawShot as any;
  const base = normalizedText(displayShotType(overlay));
  const strokeType = normalizedText(rawShot?.stroke_type);
  if (base === "dink" && strokeType) return `${strokeType} dink`;
  return base || "unknown";
}

function isKitchenShotForDinkReview(overlay: ShotNumberOverlay): boolean {
  const shotType = normalizedText(displayShotType(overlay));
  const derived = normalizedText(derivedKitchenShotType(overlay));
  if (shotType.includes("dink") || derived.includes("dink")) return true;
  if (shotType.includes("serve") || shotType.includes("return")) return false;
  return isKitchenContact(overlay);
}

function isForehandDinkShotType(type: string | null | undefined): boolean {
  const text = normalizedText(type);
  return text.includes("forehand") && text.includes("dink");
}

function overlayContactMs(row: KitchenArrivalRow, overlay: ShotNumberOverlay): number | null {
  const raw = overlay.rawShot;
  const candidates = [
    raw?.start_ms,
    overlay.trajectoryStart?.ms,
    typeof overlay.atMsRel === "number" ? row.segmentStartMs + overlay.atMsRel : null,
  ];
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

function shotEndMs(overlay: ShotNumberOverlay, fallback: number): number {
  const candidates = [
    overlay.rawShot?.end_ms,
    overlay.trajectoryEnd?.ms,
    fallback + 400,
  ];
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return fallback + 400;
}

function sortedShotOverlays(row: KitchenArrivalRow): ShotNumberOverlay[] {
  return [...(row.shotNumberOverlays ?? [])].sort(
    (a, b) => (a.rawShot?.start_ms ?? a.atMsRel) - (b.rawShot?.start_ms ?? b.atMsRel),
  );
}

function clipBoundsForMoment(args: {
  kind: CoachViewMomentKind;
  row: KitchenArrivalRow;
  overlays: ShotNumberOverlay[];
  overlay: ShotNumberOverlay;
  overlayIndex: number;
  contactMs: number;
}): { clipStartMs: number; clipEndMs: number } {
  const { kind, row, overlays, overlay, overlayIndex, contactMs } = args;
  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  const segmentEndMs = row.segmentEndMs ?? row.endMs ?? contactMs + 1000;
  const previous = overlays[overlayIndex - 1] ?? null;
  const next = overlays[overlayIndex + 1] ?? null;
  const previousContactMs = previous ? overlayContactMs(row, previous) : null;
  const nextContactMs = next ? overlayContactMs(row, next) : null;

  if (kind === "candidateSpeedup" || kind === "candidateBodybag" || kind === "possiblePopup") {
    const nextEndMs =
      nextContactMs != null
        ? nextContactMs + 1400
        : shotEndMs(overlay, contactMs) + 1600;
    return {
      clipStartMs: Math.max(
        segmentStartMs,
        (previousContactMs ?? contactMs) - COACH_VIEW_THRESHOLDS.speedupClipPaddingMs,
      ),
      clipEndMs: Math.min(segmentEndMs, Math.max(contactMs + 1400, nextEndMs)),
    };
  }

  if (kind === "paddleReadiness") {
    return {
      clipStartMs: Math.max(segmentStartMs, contactMs - 900),
      clipEndMs: Math.min(segmentEndMs, contactMs + 1200),
    };
  }

  if (kind === "dinkReadiness") {
    const previousStartMs =
      typeof previous?.rawShot?.start_ms === "number"
        ? previous.rawShot.start_ms
        : previousContactMs;
    const nextEndMs =
      next && nextContactMs != null ? shotEndMs(next, nextContactMs) : null;
    return {
      clipStartMs: Math.max(
        segmentStartMs,
        (previousStartMs ?? previousContactMs ?? contactMs) + 120,
      ),
      clipEndMs: Math.min(
        segmentEndMs,
        Math.max(
          contactMs + 900,
          nextEndMs ?? nextContactMs ?? shotEndMs(overlay, contactMs),
        ) + 250,
      ),
    };
  }

  if (kind === "leftSideCoverage") {
    const nextEndMs =
      next && nextContactMs != null ? shotEndMs(next, nextContactMs) : null;
    return {
      clipStartMs: Math.max(
        segmentStartMs,
        (previousContactMs ?? contactMs) -
          COACH_VIEW_THRESHOLDS.paddleClipPaddingMs,
      ),
      clipEndMs: Math.min(
        segmentEndMs,
        Math.max(
          contactMs + COACH_VIEW_THRESHOLDS.coveragePostContactMs,
          nextEndMs ?? nextContactMs ?? shotEndMs(overlay, contactMs),
        ) + COACH_VIEW_THRESHOLDS.paddleClipPaddingMs,
      ),
    };
  }

  const preContactMs =
    kind === "leftSideCoverage"
      ? COACH_VIEW_THRESHOLDS.coveragePreContactMs
      : COACH_VIEW_THRESHOLDS.preContactMs;
  const postContactMs =
    kind === "leftSideCoverage"
      ? COACH_VIEW_THRESHOLDS.coveragePostContactMs
      : COACH_VIEW_THRESHOLDS.postContactMs;

  return {
    clipStartMs: Math.max(segmentStartMs, contactMs - preContactMs),
    clipEndMs: Math.min(
      segmentEndMs,
      Math.max(contactMs + postContactMs, shotEndMs(overlay, contactMs)),
    ),
  };
}

function scoreText(row: KitchenArrivalRow): string {
  const score = row.scoreBefore;
  if (!Array.isArray(score)) return "";
  return `${score[0]}-${score[1]}`;
}

function allPlayerIds(rows: KitchenArrivalRow[]): number[] {
  const set = new Set<number>();
  for (const row of rows) {
    for (const overlay of row.shotNumberOverlays ?? []) {
      const playerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (Number.isFinite(playerId)) set.add(playerId);
    }
  }
  return Array.from(set).sort((a, b) => a - b);
}

function opposingPlayers(args: {
  hitterPlayerId: number;
  hitterTeam: 0 | 1 | null;
  playerIds: number[];
  playerIdToTeam: TeamResolver;
}): number[] {
  const { hitterPlayerId, hitterTeam, playerIds, playerIdToTeam } = args;
  const opponents = playerIds.filter((pid) => {
    if (pid === hitterPlayerId) return false;
    const team = playerIdToTeam(pid);
    return hitterTeam == null ? true : team !== hitterTeam;
  });
  return opponents.length ? opponents : playerIds.filter((pid) => pid !== hitterPlayerId);
}

function speedMph(overlay: ShotNumberOverlay): number | null {
  const candidates = [
    overlay.rawShot?.resulting_ball_movement?.speed,
    (overlay.rawShot as any)?.speed,
  ];
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

function isVolley(overlay: ShotNumberOverlay): boolean {
  return Boolean(
    overlay.rawShot?.is_volley ||
      overlay.rawShot?.resulting_ball_movement?.is_volleyed ||
      (overlay.rawShot as any)?.is_volleyed,
  );
}

function incomingBounceKind(overlay: ShotNumberOverlay): NonNullable<CoachViewMoment["incomingBounceKind"]> {
  if (isVolley(overlay)) return "volley_no_bounce";
  if (
    overlay.rawShot?.is_volley === false ||
    overlay.rawShot?.resulting_ball_movement?.is_volleyed === false ||
    (overlay.rawShot as any)?.is_volleyed === false
  ) {
    return "after_bounce";
  }
  return "unknown";
}

function incomingBounceLabel(kind: CoachViewMoment["incomingBounceKind"]): string {
  if (kind === "volley_no_bounce") return "No bounce / volley";
  if (kind === "after_bounce") return "After bounce";
  return "Bounce unknown";
}

function speedupLaneLabel(kind: CoachViewMoment["speedupLane"]): string {
  if (kind === "middle") return "Middle";
  if (kind === "down_line") return "Down line";
  if (kind === "crosscourt") return "Crosscourt";
  return "Direction unknown";
}

function speedupLane(overlay: ShotNumberOverlay): NonNullable<CoachViewMoment["speedupLane"]> {
  const start =
    overlay.trajectoryStart?.location ??
    overlay.rawShot?.resulting_ball_movement?.trajectory?.start?.location ??
    null;
  const end =
    overlay.rawShot?.resulting_ball_movement?.trajectory?.end?.location ??
    null;
  const startX = (start as any)?.x;
  const endX = (end as any)?.x;
  if (!Number.isFinite(startX) || !Number.isFinite(endX)) return "unknown";

  const sx = Number(startX);
  const ex = Number(endX);
  if (Math.abs(ex) <= 2.0) return "middle";
  if (Math.abs(ex - sx) <= 4.5) {
    return "down_line";
  }
  return "crosscourt";
}

function shotTypeLabel(type: string | null | undefined): string {
  const value = String(type ?? "").trim();
  if (!value) return "Unknown";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function PaddleReadinessIcon(props: { size?: number }) {
  const size = props.size ?? 24;
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M3.8 14.4h16.4"
          stroke="#1976d2"
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity="0.6"
        />
        <path
          d="M8.8 18.7 11.1 15"
          stroke="#1976d2"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <ellipse
          cx="13.1"
          cy="10.2"
          rx="4.2"
          ry="5.1"
          transform="rotate(-31 13.1 10.2)"
          fill="rgba(25,118,210,0.13)"
          stroke="#1976d2"
          strokeWidth="1.7"
        />
        <path
          d="M10.4 8.3c1.9 1 3.4 2.4 4.5 4.2"
          stroke="#1976d2"
          strokeWidth="1.25"
          strokeLinecap="round"
          opacity="0.55"
        />
        <path
          d="M17.6 16.3c1.1-.8 1.9-1.9 2.3-3.3"
          stroke="#1976d2"
          strokeWidth="1.45"
          strokeLinecap="round"
          opacity="0.75"
        />
        <circle cx="19" cy="7.2" r="1.7" fill="#ffb74d" stroke="#8a5a00" strokeWidth="0.8" />
      </svg>
    </Box>
  );
}

function isKitchenZone(value: unknown): boolean {
  const text = lower(value);
  return text.includes("kitchen") || text.includes("nvz") || text.includes("non_volley");
}

function isKitchenContact(overlay: ShotNumberOverlay): boolean {
  return Boolean(
    isKitchenZone(overlay.trajectoryStart?.zone) ||
      isKitchenZone(overlay.trajectoryEnd?.zone) ||
      isKitchenZone((overlay.rawShot as any)?.court_zone) ||
      isKitchenZone((overlay.rawShot as any)?.contact_zone),
  );
}

function playerCourtPosition(
  overlay: ShotNumberOverlay,
  playerId: number,
  _insightsToCvPlayerIndex?: PlayerIndexMap,
): { x: number; y: number } | null {
  const positions = overlay.player_positions ?? overlay.rawShot?.player_positions;
  if (!Array.isArray(positions)) return null;
  // PBV shot positions are keyed by the insights player id. The CV/avatar map is
  // only for display identity and must not be used for court-position lookups.
  const pos = positions[playerId];
  if (!pos || typeof pos.x !== "number" || typeof pos.y !== "number") return null;
  if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return null;
  return { x: pos.x, y: pos.y };
}

function ballContactPosition(
  overlay: ShotNumberOverlay,
): { x: number; y: number } | null {
  const candidates = [
    overlay.trajectoryStart?.location,
    (overlay.rawShot as any)?.resulting_ball_movement?.trajectory?.start?.location,
  ];
  for (const point of candidates) {
    if (!point || typeof point.x !== "number" || typeof point.y !== "number") continue;
    if (Number.isFinite(point.x) && Number.isFinite(point.y)) {
      return { x: point.x, y: point.y };
    }
  }
  return null;
}

function playerCourtZone(
  overlay: ShotNumberOverlay,
  playerId: number,
  insightsToCvPlayerIndex?: PlayerIndexMap,
): CoachViewMoment["reviewedPlayerZone"] {
  const pos = playerCourtPosition(overlay, playerId, insightsToCvPlayerIndex);
  if (!pos) return "unknown";
  const distanceFromNet = Math.abs(pos.y - COACH_VIEW_THRESHOLDS.courtNetY);
  if (
    distanceFromNet <=
    COACH_VIEW_THRESHOLDS.kitchenHalfDepthFt +
      COACH_VIEW_THRESHOLDS.kitchenPositionLeewayFt
  ) {
    return "kitchen";
  }
  if (distanceFromNet <= COACH_VIEW_THRESHOLDS.transitionMaxDistanceFromNetFt) {
    return "transition";
  }
  return "deep";
}

function zoneLabel(zone: CoachViewMoment["reviewedPlayerZone"]): string {
  if (zone === "kitchen") return "at kitchen";
  if (zone === "transition") return "in transition";
  if (zone === "deep") return "deep / baseline";
  return "position unknown";
}

function contactLocationLabel(x: number | null | undefined): string {
  if (typeof x !== "number" || !Number.isFinite(x)) return "contact unknown";
  const middleDistance = Math.abs(x - COACH_VIEW_THRESHOLDS.middleLineX);
  if (middleDistance <= COACH_VIEW_THRESHOLDS.middleBallHalfWidthFt) return "middle ball";
  if (x > COACH_VIEW_THRESHOLDS.middleLineX) return "partner-side ball";
  return "left-side ball";
}

function coverageContactLocationLabel(
  x: number | null | undefined,
  leftSideHighX: boolean,
): string {
  if (typeof x !== "number" || !Number.isFinite(x)) return "contact unknown";
  const middleDistance = Math.abs(x - COACH_VIEW_THRESHOLDS.middleLineX);
  if (middleDistance <= COACH_VIEW_THRESHOLDS.middleBallHalfWidthFt) {
    return "middle ball";
  }
  const partnerSide = leftSideHighX
    ? x < COACH_VIEW_THRESHOLDS.middleLineX
    : x > COACH_VIEW_THRESHOLDS.middleLineX;
  return partnerSide ? "partner-side ball" : "left-side ball";
}

function coverageKindLabel(
  kind: CoachViewMoment["coverageCandidateKind"],
): string {
  if (kind === "middle_take") return "Middle take";
  if (kind === "partner_side_take") return "Partner-side coverage";
  if (kind === "body_cross") return "Body crossed middle";
  if (kind === "partner_middle_ball") return "Partner middle ball";
  return "Coverage candidate";
}

function coverageOutcomeLabel(
  outcome: CoachViewMoment["coverageOutcome"],
): string {
  if (outcome === "left_side_did_not_take") return "Left-side did not take it";
  if (outcome === "left_side_took") return "Left-side took it";
  return "Review candidate";
}

function coverageOutcomeShortLabel(
  outcome: CoachViewMoment["coverageOutcome"],
): string {
  if (outcome === "left_side_did_not_take") return "Did not take it";
  if (outcome === "left_side_took") return "Took it";
  return "Review";
}

function partnerSideDistanceFromMiddleFt(
  x: number | null | undefined,
  leftSideHighX: boolean,
): number | null {
  if (typeof x !== "number" || !Number.isFinite(x)) return null;
  const distance = leftSideHighX
    ? COACH_VIEW_THRESHOLDS.middleLineX - x
    : x - COACH_VIEW_THRESHOLDS.middleLineX;
  return Math.max(0, distance);
}

function formatFeet(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)} ft`;
}

function shotPhaseLabel(shotNumber: number): string {
  if (shotNumber === 1) return "Serve";
  if (shotNumber === 2) return "Return";
  if (shotNumber === 3) return "3rd shot";
  if (shotNumber === 4) return "4th shot";
  if (shotNumber === 5) return "5th shot";
  return "Rally exchange";
}

function gameStageForMoment(args: {
  overlay: ShotNumberOverlay;
  reviewedPlayerId: number;
  hitterPlayerId: number;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): {
  key: string;
  label: string;
  reviewedZone: CoachViewMoment["reviewedPlayerZone"];
  hitterZone: CoachViewMoment["hitterPlayerZone"];
} {
  const { overlay, reviewedPlayerId, hitterPlayerId } = args;
  const reviewedZone = playerCourtZone(
    overlay,
    reviewedPlayerId,
    args.insightsToCvPlayerIndex,
  );
  const hitterZone = playerCourtZone(
    overlay,
    hitterPlayerId,
    args.insightsToCvPlayerIndex,
  );
  const phase = shotPhaseLabel(overlay.shotNumber);

  if (overlay.shotNumber >= 6 && reviewedZone === "kitchen" && hitterZone === "kitchen") {
    return {
      key: "kitchen_exchange",
      label: "Kitchen exchange",
      reviewedZone,
      hitterZone,
    };
  }

  const zonePart = reviewedZone === "unknown" ? "unknown" : reviewedZone;
  return {
    key: `${phase.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${zonePart}`,
    label: `${phase} · ${zoneLabel(reviewedZone)}`,
    reviewedZone,
    hitterZone,
  };
}

function explicitSpeedupLabel(overlay: ShotNumberOverlay): boolean {
  const pieces = [
    overlay.isSpeedup,
    (overlay.rawShot as any)?.is_speedup,
    (overlay.rawShot as any)?.speedup,
    overlay.rawShot?.shot_type,
    overlay.shotType,
    overlay.label,
    overlay.featuresLabel,
    ...(overlay.storyKinds ?? []),
    ...Object.keys(overlay.rawShot?.tags ?? {}),
  ];
  return pieces.some((piece) => {
    if (piece === true) return true;
    const text = lower(piece);
    return text.includes("speedup") || text.includes("attack");
  });
}

function fastExchangeMs(overlays: ShotNumberOverlay[], index: number, contactMs: number): number | null {
  const prev = overlays[index - 1];
  if (!prev) return null;
  const prevContact = prev.rawShot?.start_ms ?? prev.trajectoryStart?.ms;
  if (typeof prevContact !== "number") return null;
  const dt = contactMs - prevContact;
  return Number.isFinite(dt) && dt > 0 ? dt : null;
}

function speedupReason(args: {
  overlay: ShotNumberOverlay;
  overlays: ShotNumberOverlay[];
  index: number;
  contactMs: number;
}): string | null {
  const { overlay, overlays, index, contactMs } = args;
  const reasons: string[] = [];
  const mph = speedMph(overlay);

  if (explicitSpeedupLabel(overlay)) reasons.push("PB Vision/shot label suggests possible speedup");
  if (mph != null && mph >= COACH_VIEW_THRESHOLDS.candidateSpeedupMph) {
    reasons.push(`possible speedup shape: ${Math.round(mph)} mph shot`);
  }
  if (
    isVolley(overlay) &&
    isKitchenContact(overlay) &&
    (mph == null || mph >= COACH_VIEW_THRESHOLDS.kitchenAttackMph)
  ) {
    reasons.push("kitchen-zone possible speedup");
  }

  const exchangeMs = fastExchangeMs(overlays, index, contactMs);
  if (exchangeMs != null && exchangeMs <= COACH_VIEW_THRESHOLDS.fastExchangeMs) {
    reasons.push(`quick exchange: ${Math.round(exchangeMs)} ms after prior contact`);
  }

  return reasons.length ? reasons.join("; ") : null;
}

function isExcludedSpeedupShotLabel(overlay: ShotNumberOverlay): boolean {
  const text = overlayTextBag(overlay);
  return (
    text.includes("atp") ||
    text.includes("around the post") ||
    text.includes("dink") ||
    text.includes("drop")
  );
}

function explicitBodybagLabel(overlay: ShotNumberOverlay): string | null {
  const text = overlayTextBag(overlay);
  if (text.includes("bodybag") || text.includes("body bag")) {
    return "PB Vision/shot text suggests bodybag";
  }
  if (text.includes("tagged") || text.includes("tag ")) {
    return "PB Vision/shot text suggests player tag";
  }
  if (text.includes("jam") || text.includes("handcuff") || text.includes("chicken wing")) {
    return "PB Vision/shot text suggests jammed body contact";
  }
  if (text.includes("body shot") || text.includes("body")) {
    return "PB Vision/shot text suggests body-line contact";
  }
  return null;
}

function isExcludedBodybagShotLabel(overlay: ShotNumberOverlay): boolean {
  const text = overlayTextBag(overlay);
  return (
    text.includes("serve") ||
    text.includes("return") ||
    text.includes("atp") ||
    text.includes("around the post") ||
    text.includes("dink") ||
    text.includes("drop") ||
    text.includes("lob")
  );
}

function trajectoryEndPosition(overlay: ShotNumberOverlay): { x: number; y: number } | null {
  const rawShot = overlay.rawShot as any;
  const candidates = [
    overlay.trajectoryEnd?.location,
    rawShot?.resulting_ball_movement?.trajectory?.end?.location,
    rawShot?.trajectory?.end?.location,
    rawShot?.end_location,
  ];
  for (const candidate of candidates) {
    const x = Number(candidate?.x);
    const y = Number(candidate?.y);
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }
  return null;
}

function nearestOpponentToBallEnd(args: {
  overlay: ShotNumberOverlay;
  hitterPlayerId: number;
  playerIds: number[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): { playerId: number; distanceFt: number } | null {
  const end = trajectoryEndPosition(args.overlay);
  if (!end) return null;
  const hitterTeam = args.playerIdToTeam(args.hitterPlayerId);
  let best: { playerId: number; distanceFt: number } | null = null;

  for (const playerId of args.playerIds) {
    if (playerId === args.hitterPlayerId) continue;
    const team = args.playerIdToTeam(playerId);
    if (hitterTeam != null && team === hitterTeam) continue;
    const position = playerCourtPosition(
      args.overlay,
      playerId,
      args.insightsToCvPlayerIndex,
    );
    if (!position) continue;
    const distanceFt = Math.hypot(position.x - end.x, position.y - end.y);
    if (!Number.isFinite(distanceFt)) continue;
    if (!best || distanceFt < best.distanceFt) {
      best = { playerId, distanceFt };
    }
  }

  return best;
}

function bodybagFollowupReason(args: {
  overlays: ShotNumberOverlay[];
  index: number;
  hitterPlayerId: number;
  playerIdToTeam: TeamResolver;
  contactMs: number;
}): string | null {
  const { overlays, index, hitterPlayerId, playerIdToTeam, contactMs } = args;
  const hitterTeam = playerIdToTeam(hitterPlayerId);
  const nextOpponent = overlays.slice(index + 1).find((candidate) => {
    const nextPlayerId = Number(candidate.playerId ?? candidate.rawShot?.player_id);
    if (!Number.isFinite(nextPlayerId)) return false;
    const nextTeam = playerIdToTeam(nextPlayerId);
    return hitterTeam == null ? nextPlayerId !== hitterPlayerId : nextTeam !== hitterTeam;
  });
  if (nextOpponent) {
    const nextContact = nextOpponent.rawShot?.start_ms ?? nextOpponent.trajectoryStart?.ms;
    if (isVolley(nextOpponent)) return "next opponent touch is a volley";
    if (
      typeof nextContact === "number" &&
      Number.isFinite(nextContact) &&
      nextContact > contactMs &&
      nextContact - contactMs <= 900
    ) {
      return "next opponent touch arrives quickly";
    }
    return null;
  }
  return shotEndedPointInOrOut(overlays[index])
    ? "shot ends the point before an opponent touch"
    : null;
}

function bodybagCandidateReason(args: {
  overlay: ShotNumberOverlay;
  overlays: ShotNumberOverlay[];
  index: number;
  contactMs: number;
  hitterPlayerId: number;
  playerIds: number[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): string | null {
  const reasons: string[] = [];
  const explicit = explicitBodybagLabel(args.overlay);
  const mph = speedMph(args.overlay);
  const nearestOpponent = nearestOpponentToBallEnd(args);
  const followupReason = bodybagFollowupReason(args);

  if (explicit) reasons.push(explicit);
  if (mph != null && mph >= COACH_VIEW_THRESHOLDS.candidateBodybagMph) {
    reasons.push(`${Math.round(mph)} mph body-line candidate`);
  }
  if (
    nearestOpponent &&
    nearestOpponent.distanceFt <= COACH_VIEW_THRESHOLDS.bodybagOpponentDistanceFt
  ) {
    reasons.push(
      `estimated endpoint within ${nearestOpponent.distanceFt.toFixed(1)} ft of opponent`,
    );
  }
  if (followupReason) reasons.push(followupReason);

  if (explicit) return reasons.join("; ");
  if (
    mph != null &&
    mph >= COACH_VIEW_THRESHOLDS.candidateBodybagMph &&
    nearestOpponent &&
    nearestOpponent.distanceFt <= COACH_VIEW_THRESHOLDS.bodybagOpponentDistanceFt &&
    followupReason
  ) {
    return reasons.join("; ");
  }

  return null;
}

function isDeepOrBaselineStage(stage: {
  key: string;
  label: string;
  reviewedZone: string;
  hitterZone: string;
}): boolean {
  const text = normalizedText(`${stage.key} ${stage.label} ${stage.reviewedZone} ${stage.hitterZone}`);
  return text.includes("deep") || text.includes("baseline");
}

function shotEndedPointInOrOut(overlay: ShotNumberOverlay): boolean {
  const rawShot = overlay.rawShot as any;
  const text = overlayTextBag(overlay);
  const explicit = [
    rawShot?.ended_point,
    rawShot?.point_ended,
    rawShot?.is_point_ending,
    rawShot?.is_terminal,
  ].some(Boolean);
  if (explicit) return true;
  return (
    text.includes("winner") ||
    text.includes("out") ||
    text.includes("fault") ||
    text.includes("error") ||
    text.includes("clean")
  );
}

function speedupFollowupReason(args: {
  overlays: ShotNumberOverlay[];
  index: number;
  hitterPlayerId: number;
  playerIdToTeam: TeamResolver;
}): string | null {
  const { overlays, index, hitterPlayerId, playerIdToTeam } = args;
  const hitterTeam = playerIdToTeam(hitterPlayerId);
  const nextOpponent = overlays.slice(index + 1).find((candidate) => {
    const nextPlayerId = Number(candidate.playerId ?? candidate.rawShot?.player_id);
    if (!Number.isFinite(nextPlayerId)) return false;
    const nextTeam = playerIdToTeam(nextPlayerId);
    return hitterTeam == null ? nextPlayerId !== hitterPlayerId : nextTeam !== hitterTeam;
  });
  if (nextOpponent) {
    return isVolley(nextOpponent) ? "next opponent touch is a volley" : null;
  }
  return shotEndedPointInOrOut(overlays[index])
    ? "shot ends the point before an opponent touch"
    : null;
}

function makeMomentBase(args: {
  kind: CoachViewMomentKind;
  packageId: string;
  row: KitchenArrivalRow;
  overlay: ShotNumberOverlay;
  overlays: ShotNumberOverlay[];
  overlayIndex: number;
  hitterPlayerId: number;
  reviewedPlayerId: number;
  partnerPlayerId?: number;
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
  contactMs: number;
  candidateReason?: string;
  candidateScore?: number;
  shotTypeOverride?: string;
  coverageCandidateKind?: CoachViewMoment["coverageCandidateKind"];
  speedupLane?: CoachViewMoment["speedupLane"];
  contactLocationLabel?: string;
  coverageOutcome?: CoachViewMoment["coverageOutcome"];
  coveragePartnerSideDistanceFt?: number | null;
}): CoachViewMoment {
  const {
    kind,
    packageId,
    row,
    overlay,
    overlays,
    overlayIndex,
    hitterPlayerId,
    reviewedPlayerId,
    partnerPlayerId,
    playerIdToTeam,
    insightsToCvPlayerIndex,
    contactMs,
    candidateReason,
    candidateScore,
    shotTypeOverride,
    coverageCandidateKind,
    speedupLane: explicitSpeedupLane,
    contactLocationLabel,
    coverageOutcome,
    coveragePartnerSideDistanceFt,
  } = args;
  const { clipStartMs, clipEndMs } = clipBoundsForMoment({
    kind,
    row,
    overlays,
    overlay,
    overlayIndex,
    contactMs,
  });

  const hitterTeam = playerIdToTeam(hitterPlayerId);
  const reviewedTeam = playerIdToTeam(reviewedPlayerId);
  const shotType = shotTypeOverride ?? displayShotType(overlay);
  const gameStage = gameStageForMoment({
    overlay,
    reviewedPlayerId,
    hitterPlayerId,
    insightsToCvPlayerIndex,
  });

  return {
    kind,
    key: `${kind}_r${row.rallyIndex}_s${overlay.shotNumber}_h${hitterPlayerId}_rp${reviewedPlayerId}`,
    gameId: packageId,
    rallyId: String(row.pointKey ?? row.rallyIndex),
    rallyIndex: row.rallyIndex,
    shotId: overlay.shotKey ?? `${row.rallyIndex}:${overlay.shotNumber}`,
    shotIndex: overlayIndex,
    shotNumber: overlay.shotNumber,
    reviewedPlayerId,
    hitterPlayerId,
    partnerPlayerId,
    hitterTeam,
    reviewedTeam,
    shotType,
    rallyWinnerTeamId: row.winnerTeam ?? null,
    servingTeam: row.servingTeam ?? null,
    receivingTeam:
      row.servingTeam == null ? null : row.servingTeam === 0 ? 1 : 0,
    score: scoreText(row),
    gameStageKey: gameStage.key,
    gameStageLabel: gameStage.label,
    reviewedPlayerZone: gameStage.reviewedZone,
    hitterPlayerZone: gameStage.hitterZone,
    incomingBounceKind:
      kind === "candidateSpeedup" || kind === "candidateBodybag"
        ? incomingBounceKind(overlay)
        : undefined,
    speedupLane:
      kind === "candidateSpeedup" || kind === "candidateBodybag"
        ? explicitSpeedupLane ?? speedupLane(overlay)
        : undefined,
    coverageCandidateKind,
    contactLocationLabel,
    coverageOutcome,
    coveragePartnerSideDistanceFt,
    contactTimestamp: contactMs,
    contactFrame:
      typeof overlay.frameIdx === "number" && Number.isFinite(overlay.frameIdx)
        ? overlay.frameIdx
        : undefined,
    candidateReason,
    candidateScore,
    thumbnailUrl: undefined,
    clipStartMs,
    clipEndMs: Math.max(clipStartMs + 500, clipEndMs),
  };
}

export function buildCandidateSpeedupMoments(args: {
  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): CoachViewMoment[] {
  const out: CoachViewMoment[] = [];

  for (const row of args.rows) {
    const overlays = sortedShotOverlays(row);
    overlays.forEach((overlay, index) => {
      if (overlay.shotNumber <= 3) return;
      if (isExcludedSpeedupShotLabel(overlay)) return;
      const hitterPlayerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (!Number.isFinite(hitterPlayerId)) return;
      const contactMs = overlayContactMs(row, overlay);
      if (contactMs == null) return;
      const gameStage = gameStageForMoment({
        overlay,
        reviewedPlayerId: hitterPlayerId,
        hitterPlayerId,
        insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
      });
      if (isDeepOrBaselineStage(gameStage)) return;
      if (gameStage.hitterZone !== "kitchen") return;
      const candidateReason = speedupReason({ overlay, overlays, index, contactMs });
      if (!candidateReason) return;
      const followupReason = speedupFollowupReason({
        overlays,
        index,
        hitterPlayerId,
        playerIdToTeam: args.playerIdToTeam,
      });
      if (!followupReason) return;
      out.push(
        makeMomentBase({
          kind: "candidateSpeedup",
          packageId: args.packageId,
          row,
          overlay,
          overlays,
          overlayIndex: index,
          hitterPlayerId,
          reviewedPlayerId: hitterPlayerId,
          playerIdToTeam: args.playerIdToTeam,
          insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
          contactMs,
          candidateReason: `${candidateReason}; ${followupReason}`,
          speedupLane: speedupLane(overlay),
        }),
      );
    });
  }

  return out;
}

function possiblePopupEvidence(
  overlay: ShotNumberOverlay,
): { reason: string; score: number } | null {
  const rawShot = overlay.rawShot as any;
  const shotType = normalizedText(displayShotType(overlay));
  if (
    !shotType ||
    ["serve", "return", "drive", "smash", "atp", "erne", "lob"].some((label) =>
      shotType.includes(label),
    ) ||
    rawShot?.is_final === true
  ) {
    return null;
  }

  const movement = rawShot?.resulting_ball_movement;
  const heightOverNet = Number(movement?.height_over_net);
  const peakHeight = Number(movement?.trajectory?.peak?.z);
  const speed = Number(movement?.speed);
  if (![heightOverNet, peakHeight, speed].every(Number.isFinite)) return null;

  const isDink = shotType.includes("dink");
  const isDrop = shotType.includes("drop");
  const isResetLike = ["reset", "block", "defensive", "volley", "lift"].some((label) =>
    shotType.includes(label),
  );
  if (!isDink && !isDrop && !isResetLike) return null;

  const riseAfterNet = peakHeight - heightOverNet;
  const heightThreshold = isDink
    ? COACH_VIEW_THRESHOLDS.possiblePopupDinkHeightOverNetFt
    : isDrop
      ? COACH_VIEW_THRESHOLDS.possiblePopupDropHeightOverNetFt
      : COACH_VIEW_THRESHOLDS.possiblePopupOtherHeightOverNetFt;
  let score = 0;
  const reasons: string[] = [];

  if (heightOverNet >= heightThreshold) {
    score += 1.2;
    reasons.push(`${heightOverNet.toFixed(1)} ft over net`);
  }
  const peakThreshold = isDink ? 4.5 : isDrop ? 6.0 : 4.8;
  const slowThreshold = isDink ? 18 : isDrop ? 22 : 20;
  if (peakHeight >= peakThreshold) {
    score += 0.9;
    reasons.push(`${peakHeight.toFixed(1)} ft peak`);
  }
  if (speed <= slowThreshold) {
    score += 0.65;
    reasons.push(`${speed.toFixed(1)} mph`);
  }
  if (riseAfterNet >= 0.8) {
    score += 0.4;
    reasons.push(`${riseAfterNet.toFixed(1)} ft rise after net`);
  }

  const minimumScore = 2.1;
  if (score < minimumScore || heightOverNet < heightThreshold) return null;
  return {
    score,
    reason: `Measured candidate (${score.toFixed(2)}): ${reasons.join(" · ")}`,
  };
}

function possiblePopupCourtState(
  overlay: ShotNumberOverlay,
  playerIds: number[],
): CoachViewMoment["possiblePopupCourtState"] {
  const zones = playerIds
    .map((playerId) => playerCourtZone(overlay, playerId))
    .filter((zone) => zone !== "unknown");
  if (zones.length < 4) return "unknown";
  if (zones.every((zone) => zone === "kitchen")) return "all_kitchen";
  if (zones.some((zone) => zone === "transition" || zone === "deep")) {
    return "transitioning";
  }
  return "unknown";
}

export function buildPossiblePopupMoments(args: {
  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): CoachViewMoment[] {
  const out: CoachViewMoment[] = [];
  const playerIds = allPlayerIds(args.rows);
  for (const row of args.rows) {
    const overlays = sortedShotOverlays(row);
    overlays.forEach((overlay, index) => {
      const evidence = possiblePopupEvidence(overlay);
      if (!evidence) return;
      const hitterPlayerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (!Number.isFinite(hitterPlayerId)) return;
      const contactMs = overlayContactMs(row, overlay);
      if (contactMs == null) return;
      const moment = makeMomentBase({
          kind: "possiblePopup",
          packageId: args.packageId,
          row,
          overlay,
          overlays,
          overlayIndex: index,
          hitterPlayerId,
          reviewedPlayerId: hitterPlayerId,
          playerIdToTeam: args.playerIdToTeam,
          insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
          contactMs,
          candidateReason: evidence.reason,
          candidateScore: evidence.score,
        });
      const measuredCourtState = possiblePopupCourtState(overlay, playerIds);
      out.push({
        ...moment,
        possiblePopupCourtState: measuredCourtState,
      });
    });
  }
  return out.sort(
    (a, b) =>
      (b.candidateScore ?? 0) - (a.candidateScore ?? 0) ||
      a.rallyIndex - b.rallyIndex ||
      a.shotNumber - b.shotNumber,
  );
}

export function buildCandidateBodybagMoments(args: {
  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): CoachViewMoment[] {
  const playerIds = allPlayerIds(args.rows);
  const out: CoachViewMoment[] = [];

  for (const row of args.rows) {
    const overlays = sortedShotOverlays(row);
    overlays.forEach((overlay, index) => {
      if (overlay.shotNumber <= 2) return;
      if (isExcludedBodybagShotLabel(overlay)) return;
      const hitterPlayerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (!Number.isFinite(hitterPlayerId)) return;
      const contactMs = overlayContactMs(row, overlay);
      if (contactMs == null) return;
      const gameStage = gameStageForMoment({
        overlay,
        reviewedPlayerId: hitterPlayerId,
        hitterPlayerId,
        insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
      });
      if (!explicitBodybagLabel(overlay) && gameStage.hitterZone === "deep") return;
      const candidateReason = bodybagCandidateReason({
        overlay,
        overlays,
        index,
        contactMs,
        hitterPlayerId,
        playerIds,
        playerIdToTeam: args.playerIdToTeam,
        insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
      });
      if (!candidateReason) return;
      out.push(
        makeMomentBase({
          kind: "candidateBodybag",
          packageId: args.packageId,
          row,
          overlay,
          overlays,
          overlayIndex: index,
          hitterPlayerId,
          reviewedPlayerId: hitterPlayerId,
          playerIdToTeam: args.playerIdToTeam,
          insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
          contactMs,
          candidateReason,
          speedupLane: speedupLane(overlay),
        }),
      );
    });
  }

  return out;
}

export function buildPaddleReadinessMoments(args: {
  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): CoachViewMoment[] {
  const playerIds = allPlayerIds(args.rows);
  const out: CoachViewMoment[] = [];

  for (const row of args.rows) {
    const overlays = sortedShotOverlays(row);
    overlays.forEach((overlay, index) => {
      const hitterPlayerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (!Number.isFinite(hitterPlayerId)) return;
      const contactMs = overlayContactMs(row, overlay);
      if (contactMs == null) return;
      const hitterTeam = args.playerIdToTeam(hitterPlayerId);
      const reviewedPlayers = opposingPlayers({
        hitterPlayerId,
        hitterTeam,
        playerIds,
        playerIdToTeam: args.playerIdToTeam,
      });
      for (const reviewedPlayerId of reviewedPlayers) {
        out.push(
          makeMomentBase({
            kind: "paddleReadiness",
            packageId: args.packageId,
            row,
            overlay,
            overlays,
            overlayIndex: index,
            hitterPlayerId,
            reviewedPlayerId,
            playerIdToTeam: args.playerIdToTeam,
            insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
            contactMs,
            candidateReason: "Opponent contact frame for readiness review",
          }),
        );
      }
    });
  }

  return out;
}

export function buildDinkReadinessMoments(args: {
  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): CoachViewMoment[] {
  const playerIds = allPlayerIds(args.rows);
  const out: CoachViewMoment[] = [];

  for (const row of args.rows) {
    const overlays = sortedShotOverlays(row);
    overlays.forEach((overlay, index) => {
      if (!isKitchenShotForDinkReview(overlay)) return;
      const hitterPlayerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (!Number.isFinite(hitterPlayerId)) return;
      const contactMs = overlayContactMs(row, overlay);
      if (contactMs == null) return;
      const hitterTeam = args.playerIdToTeam(hitterPlayerId);
      const reviewedPlayers = opposingPlayers({
        hitterPlayerId,
        hitterTeam,
        playerIds,
        playerIdToTeam: args.playerIdToTeam,
      });
      const shotTypeOverride = derivedKitchenShotType(overlay);
      for (const reviewedPlayerId of reviewedPlayers) {
        out.push(
          makeMomentBase({
            kind: "dinkReadiness",
            packageId: args.packageId,
            row,
            overlay,
            overlays,
            overlayIndex: index,
            hitterPlayerId,
            reviewedPlayerId,
            playerIdToTeam: args.playerIdToTeam,
            insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
            contactMs,
            shotTypeOverride,
            candidateReason: "Dink response review",
          }),
        );
      }
    });
  }

  return out;
}

function teamPlayerIdsForShot(args: {
  overlay: ShotNumberOverlay;
  hitterPlayerId: number;
  playerIds: number[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): number[] {
  const {
    overlay,
    hitterPlayerId,
    playerIds,
    playerIdToTeam,
    insightsToCvPlayerIndex,
  } = args;
  const hitterTeam = playerIdToTeam(hitterPlayerId);
  const sameTeam = playerIds.filter((pid) => {
    if (pid === hitterPlayerId) return true;
    const team = playerIdToTeam(pid);
    return hitterTeam == null ? false : team === hitterTeam;
  });
  const tracked = sameTeam.filter((pid) =>
    playerCourtPosition(overlay, pid, insightsToCvPlayerIndex),
  );
  const leftSideHighX = teamLeftSideIsHighX(
    overlay,
    tracked,
    insightsToCvPlayerIndex,
  );
  return tracked.sort((a, b) => {
    const ax =
      playerCourtPosition(overlay, a, insightsToCvPlayerIndex)?.x ??
      Number.POSITIVE_INFINITY;
    const bx =
      playerCourtPosition(overlay, b, insightsToCvPlayerIndex)?.x ??
      Number.POSITIVE_INFINITY;
    return leftSideHighX ? bx - ax : ax - bx;
  });
}

function teamLeftSideIsHighX(
  overlay: ShotNumberOverlay,
  playerIds: number[],
  insightsToCvPlayerIndex?: PlayerIndexMap,
): boolean {
  const positions = playerIds
    .map((pid) => playerCourtPosition(overlay, pid, insightsToCvPlayerIndex))
    .filter((pos): pos is { x: number; y: number } => pos != null);
  if (!positions.length) return false;
  const avgY =
    positions.reduce((sum, pos) => sum + pos.y, 0) / positions.length;

  // Court x is fixed in camera/world coordinates, but player "left side"
  // flips with which side of the net the team is on.
  return avgY < COACH_VIEW_THRESHOLDS.courtNetY;
}

function coverageCandidateForShot(args: {
  overlay: ShotNumberOverlay;
  hitterPlayerId: number;
  playerIds: number[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): {
  reviewedPlayerId: number;
  partnerPlayerId: number;
  kind: NonNullable<CoachViewMoment["coverageCandidateKind"]>;
  reason: string;
  contactLabel: string;
  outcome: NonNullable<CoachViewMoment["coverageOutcome"]>;
  partnerSideDistanceFt: number | null;
} | null {
  const {
    overlay,
    hitterPlayerId,
    playerIds,
    playerIdToTeam,
    insightsToCvPlayerIndex,
  } = args;
  const teamPlayers = teamPlayerIdsForShot({
    overlay,
    hitterPlayerId,
    playerIds,
    playerIdToTeam,
    insightsToCvPlayerIndex,
  });
  if (teamPlayers.length < 2) return null;

  const leftPlayerId = teamPlayers[0]!;
  const partnerPlayerId = teamPlayers.find((pid) => pid !== leftPlayerId);
  if (partnerPlayerId == null) return null;

  const leftPos = playerCourtPosition(
    overlay,
    leftPlayerId,
    insightsToCvPlayerIndex,
  );
  if (!leftPos) return null;

  const contact =
    ballContactPosition(overlay) ??
    playerCourtPosition(overlay, hitterPlayerId, insightsToCvPlayerIndex);
  const contactX = contact?.x;
  const leftSideHighX = teamLeftSideIsHighX(
    overlay,
    teamPlayers,
    insightsToCvPlayerIndex,
  );
  const contactLabel = coverageContactLocationLabel(contactX, leftSideHighX);
  const partnerSideDistanceFt = partnerSideDistanceFromMiddleFt(
    contactX,
    leftSideHighX,
  );
  const isMiddleBall =
    typeof contactX === "number" &&
    Math.abs(contactX - COACH_VIEW_THRESHOLDS.middleLineX) <=
      COACH_VIEW_THRESHOLDS.middleBallHalfWidthFt;
  const isPartnerSideBall =
    typeof contactX === "number" &&
    (leftSideHighX
      ? contactX < COACH_VIEW_THRESHOLDS.middleLineX
      : contactX > COACH_VIEW_THRESHOLDS.middleLineX);
  const leftBodyCrossed = leftSideHighX
    ? leftPos.x < COACH_VIEW_THRESHOLDS.middleLineX
    : leftPos.x > COACH_VIEW_THRESHOLDS.middleLineX;

  if (hitterPlayerId === leftPlayerId) {
    if (isPartnerSideBall) {
      return {
        reviewedPlayerId: leftPlayerId,
        partnerPlayerId,
        kind: "partner_side_take",
        reason: `Left-side player took it: partner-side coverage candidate on a ${contactLabel}`,
        contactLabel,
        outcome: "left_side_took",
        partnerSideDistanceFt,
      };
    }
    if (leftBodyCrossed && (isMiddleBall || isPartnerSideBall)) {
      return {
        reviewedPlayerId: leftPlayerId,
        partnerPlayerId,
        kind: "body_cross",
        reason: `Left-side player took it: body position crossed the middle line on a ${contactLabel}`,
        contactLabel,
        outcome: "left_side_took",
        partnerSideDistanceFt,
      };
    }
    if (isMiddleBall) {
      return {
        reviewedPlayerId: leftPlayerId,
        partnerPlayerId,
        kind: "middle_take",
        reason: `Left-side player took it: middle-take candidate on a ${contactLabel}`,
        contactLabel,
        outcome: "left_side_took",
        partnerSideDistanceFt,
      };
    }
  }

  if (hitterPlayerId === partnerPlayerId && isMiddleBall) {
    return {
      reviewedPlayerId: leftPlayerId,
      partnerPlayerId,
      kind: "partner_middle_ball",
      reason: `Left-side player did not take it: partner played a ${contactLabel}`,
      contactLabel,
      outcome: "left_side_did_not_take",
      partnerSideDistanceFt,
    };
  }

  return null;
}

export function buildLeftSideCoverageMoments(args: {
  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: TeamResolver;
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): CoachViewMoment[] {
  const playerIds = allPlayerIds(args.rows);
  const out: CoachViewMoment[] = [];

  for (const row of args.rows) {
    const overlays = sortedShotOverlays(row);
    overlays.forEach((overlay, index) => {
      if (overlay.shotNumber <= 2) return;
      const hitterPlayerId = Number(overlay.playerId ?? overlay.rawShot?.player_id);
      if (!Number.isFinite(hitterPlayerId)) return;
      const contactMs = overlayContactMs(row, overlay);
      if (contactMs == null) return;
      const candidate = coverageCandidateForShot({
        overlay,
        hitterPlayerId,
        playerIds,
        playerIdToTeam: args.playerIdToTeam,
        insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
      });
      if (!candidate) return;
      out.push(
        makeMomentBase({
          kind: "leftSideCoverage",
          packageId: args.packageId,
          row,
          overlay,
          overlays,
          overlayIndex: index,
          hitterPlayerId,
          reviewedPlayerId: candidate.reviewedPlayerId,
          partnerPlayerId: candidate.partnerPlayerId,
          playerIdToTeam: args.playerIdToTeam,
          insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
          contactMs,
          candidateReason: candidate.reason,
          coverageCandidateKind: candidate.kind,
          contactLocationLabel: candidate.contactLabel,
          coverageOutcome: candidate.outcome,
          coveragePartnerSideDistanceFt: candidate.partnerSideDistanceFt,
        }),
      );
    });
  }

  return out;
}

function uniqueNumbers(values: Array<number | null | undefined>): number[] {
  return Array.from(
    new Set(values.filter((value): value is number => typeof value === "number")),
  ).sort((a, b) => a - b);
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(
      values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value)),
    ),
  ).sort((a, b) => a.localeCompare(b));
}

function PlayerMini(props: {
  playerId: number;
  renderPlayerIdentity?: RenderPlayerIdentity;
  insightsToCvPlayerIndex?: PlayerIndexMap;
  highlighted?: boolean;
  size?: number;
  fontColor?: string;
  fontSize?: number;
}) {
  const {
    playerId,
    renderPlayerIdentity,
    highlighted,
    size = 34,
    fontColor = "black",
    fontSize = 12,
  } = props;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 1,
        outline: highlighted ? "2px solid #1976d2" : "none",
        outlineOffset: 2,
        boxShadow: highlighted ? "0 0 0 4px rgba(25,118,210,0.12)" : "none",
      }}
    >
      {renderPlayerIdentity ? (
        renderPlayerIdentity({
          playerId,
          labelOnly: false,
          size,
          borderRadius: 5,
          fontSize,
          fontColor,
        })
      ) : (
        <Box sx={{ fontWeight: 900, color: fontColor }}>p{playerId}</Box>
      )}
    </Box>
  );
}

function FilterChipGroup(props: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  minButtonWidth?: number;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 950, opacity: 0.62, mb: 0.25 }}>
        {props.label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.35,
          alignItems: "center",
        }}
      >
        {props.options.map((option) => (
          <Button
            key={option.value}
            size="small"
            variant={props.value === option.value ? "contained" : "outlined"}
            onClick={() => props.onChange(option.value)}
            sx={{
              minWidth: props.minButtonWidth ?? 0,
              height: 26,
              px: 0.75,
              py: 0,
              textTransform: "none",
              fontSize: 11,
              lineHeight: 1,
              fontWeight: 900,
              borderRadius: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

function PlayerFilterChipGroup(props: {
  label?: string;
  value: string;
  playerIds: number[];
  onChange: (value: string) => void;
  renderPlayerIdentity?: RenderPlayerIdentity;
  insightsToCvPlayerIndex?: PlayerIndexMap;
  playerSuffix?: (playerId: number) => string | null | undefined;
  avatarSize?: number;
  buttonHeight?: number;
  fontSize?: number;
}) {
  const avatarSize = props.avatarSize ?? 26;
  const buttonHeight = props.buttonHeight ?? 26;
  const fontSize = props.fontSize ?? 10;
  return (
    <Box sx={{ minWidth: 0 }}>
      {props.label ? (
        <Typography sx={{ fontSize: 10, fontWeight: 950, opacity: 0.62, mb: 0.25 }}>
          {props.label}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.35 }}>
        <Button
          size="small"
          variant={props.value === "all" ? "contained" : "outlined"}
          onClick={() => props.onChange("all")}
          sx={{
            height: 26,
            px: 0.8,
            py: 0,
            textTransform: "none",
            fontSize: 11,
            fontWeight: 950,
            borderRadius: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          All
        </Button>
        {props.playerIds.map((pid) => (
          <Button
            key={pid}
            size="small"
            variant={props.value === String(pid) ? "contained" : "outlined"}
            onClick={() => props.onChange(String(pid))}
            sx={{
              height: buttonHeight,
              minWidth: 0,
              px: 0.45,
              py: 0,
              textTransform: "none",
              borderRadius: 1.2,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <PlayerMini
              playerId={pid}
              renderPlayerIdentity={props.renderPlayerIdentity}
              insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
              size={avatarSize}
              fontSize={Math.max(11, fontSize)}
            />
            {props.playerSuffix ? (
              <Typography
                component="span"
                sx={{
                  ml: 0.35,
                  fontSize,
                  fontWeight: 950,
                  color: props.value === String(pid) ? "white" : "#1976d2",
                  lineHeight: 1,
                }}
              >
                {props.playerSuffix(pid)}
              </Typography>
            ) : null}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

function defaultGameStageFilterForKind(
  kind: CoachViewMomentKind,
  moments: CoachViewMoment[],
): string {
  if (
    ((kind === "candidateSpeedup" || kind === "candidateBodybag" || kind === "paddleReadiness")) &&
    moments.some((moment) => moment.gameStageKey === "kitchen_exchange")
  ) {
    return "kitchen_exchange";
  }
  return "all";
}

function defaultShotTypeFilterForKind(
  kind: CoachViewMomentKind,
  moments: CoachViewMoment[],
): string {
  if (kind !== "dinkReadiness") return "all";
  const forehandDink = moments.find((moment) =>
    isForehandDinkShotType(moment.shotType),
  )?.shotType;
  if (forehandDink) return forehandDink;
  const dink = moments.find((moment) =>
    normalizedText(moment.shotType).includes("dink"),
  )?.shotType;
  return dink ?? "all";
}

function coachMomentLensTitle(moment: CoachViewMoment): string {
  if (moment.kind === "candidateSpeedup") {
    return `Speedup candidate: shot ${moment.shotNumber}`;
  }
  if (moment.kind === "candidateBodybag") {
    return `Bodybag candidate: shot ${moment.shotNumber}`;
  }
  if (moment.kind === "possiblePopup") {
    return `Possible popup: shot ${moment.shotNumber}`;
  }
  if (moment.kind === "paddleReadiness") {
    return "Inspect readiness at opponent contact";
  }
  if (moment.kind === "dinkReadiness") {
    return `Dink response: shot ${moment.shotNumber}`;
  }
  return coverageOutcomeLabel(moment.coverageOutcome);
}

function activeRallyLensKeyForCoachMoment(moment: CoachViewMoment): string {
  const lens: ActiveRallyLensMoment["lens"] =
    moment.kind === "candidateSpeedup"
      ? "candidateSpeedups"
      : moment.kind === "candidateBodybag"
        ? "candidateBodybags"
      : moment.kind === "possiblePopup"
        ? "possiblePopups"
      : moment.kind === "paddleReadiness" || moment.kind === "dinkReadiness"
        ? "paddleReadiness"
        : "leftSideCoverage";
  return `${lens}-${moment.key}`;
}

function coachMomentLensDetail(moment: CoachViewMoment): string {
  if (moment.kind === "leftSideCoverage") return "";
  if (moment.kind === "paddleReadiness" || moment.kind === "dinkReadiness") {
    return [
      `Responding to: ${shotTypeLabel(moment.shotType)}`,
      moment.gameStageLabel,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return [
    moment.candidateReason,
    moment.shotType ? `shot type: ${moment.shotType}` : null,
    moment.gameStageLabel,
  ]
    .filter(Boolean)
    .join(" · ");
}

function coachMomentLensCourtPlayers(
  overlay: ShotNumberOverlay | null | undefined,
  moment: CoachViewMoment,
  insightsToCvPlayerIndex?: PlayerIndexMap,
): ActiveRallyLensMoment["courtPlayers"] {
  const ids = [
    { playerId: moment.reviewedPlayerId, role: "reviewed" as const },
    { playerId: moment.hitterPlayerId, role: "hitter" as const },
    typeof moment.partnerPlayerId === "number"
      ? { playerId: moment.partnerPlayerId, role: "partner" as const }
      : null,
  ].filter(Boolean) as Array<{
    playerId: number;
    role: "reviewed" | "hitter" | "partner";
  }>;

  const seen = new Set<number>();
  const players: NonNullable<ActiveRallyLensMoment["courtPlayers"]> = [];
  for (const item of ids) {
    if (seen.has(item.playerId)) continue;
    seen.add(item.playerId);
    if (!overlay) continue;
    const pos = playerCourtPosition(overlay, item.playerId, insightsToCvPlayerIndex);
    if (!pos) continue;
    players.push({
      playerId: item.playerId,
      role: item.role,
      x: pos.x,
      y: pos.y,
      team:
        item.role === "hitter"
          ? moment.hitterTeam
          : item.role === "reviewed" || item.role === "partner"
            ? moment.reviewedTeam
            : null,
    });
  }
  return players;
}

function coachMomentLensShotPath(
  overlay: ShotNumberOverlay | null | undefined,
): ActiveRallyLensMoment["shotPath"] {
  if (!overlay) return undefined;
  const trajectory = (overlay.rawShot as any)?.resulting_ball_movement?.trajectory;
  const candidates = [
    overlay.trajectoryStart?.location ?? trajectory?.start?.location,
    trajectory?.peak?.location ?? trajectory?.peak,
    overlay.trajectoryEnd?.location ?? trajectory?.end?.location,
  ];
  const points: Array<{ x: number; y: number }> = [];
  for (const point of candidates) {
    const x = typeof point?.x === "number" ? point.x : null;
    const y = typeof point?.y === "number" ? point.y : null;
    if (x == null || y == null || !Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }
    const previous = points[points.length - 1];
    if (previous && Math.hypot(previous.x - x, previous.y - y) < 0.15) {
      continue;
    }
    points.push({ x, y });
  }
  return points.length >= 2 ? points : undefined;
}

function coachMomentLensShotPathEntry(args: {
  row: KitchenArrivalRow;
  overlay: ShotNumberOverlay;
  segmentStartMs: number;
  playerTeamById: Map<number, 0 | 1 | null>;
}): NonNullable<ActiveRallyLensMoment["shotPaths"]>[number] | null {
  const points = coachMomentLensShotPath(args.overlay);
  if (!points || points.length < 2) return null;

  const contactMs = overlayContactMs(args.row, args.overlay);
  if (contactMs == null) return null;

  const playerId = Number(args.overlay.playerId ?? args.overlay.rawShot?.player_id);
  const endMs = Math.max(contactMs + 250, shotEndMs(args.overlay, contactMs));
  return {
    shotNumber: args.overlay.shotNumber,
    playerId: Number.isFinite(playerId) ? playerId : undefined,
    team: Number.isFinite(playerId)
      ? args.playerTeamById.get(playerId) ?? null
      : null,
    startSec: Math.max(0, (contactMs - args.segmentStartMs) / 1000),
    endSec: Math.max(0.25, (endMs - args.segmentStartMs) / 1000),
    points,
  };
}

function buildActiveRallyLensMomentsForRow(args: {
  row: KitchenArrivalRow | null | undefined;
  moments: CoachViewMoment[];
  insightsToCvPlayerIndex?: PlayerIndexMap;
}): ActiveRallyLensMoment[] {
  const { row, moments } = args;
  if (!row) return [];

  const overlayByShotNumber = new Map<number, ShotNumberOverlay>();
  for (const overlay of row.shotNumberOverlays ?? []) {
    overlayByShotNumber.set(overlay.shotNumber, overlay);
  }

  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  const playerTeamById = new Map<number, 0 | 1 | null>();
  for (const moment of moments) {
    if (typeof moment.hitterPlayerId === "number") {
      playerTeamById.set(moment.hitterPlayerId, moment.hitterTeam);
    }
    if (typeof moment.reviewedPlayerId === "number") {
      playerTeamById.set(moment.reviewedPlayerId, moment.reviewedTeam);
    }
    if (typeof moment.partnerPlayerId === "number") {
      playerTeamById.set(moment.partnerPlayerId, moment.reviewedTeam);
    }
  }

  const rowShotPaths = sortedShotOverlays(row)
    .map((overlay) =>
      coachMomentLensShotPathEntry({
        row,
        overlay,
        segmentStartMs,
        playerTeamById,
      }),
    )
    .filter(
      (
        entry,
      ): entry is NonNullable<ActiveRallyLensMoment["shotPaths"]>[number] =>
        entry != null,
    );

  return moments
    .filter((moment) => moment.rallyIndex === row.rallyIndex)
    .map((moment) => {
      const lens: ActiveRallyLensMoment["lens"] =
        moment.kind === "candidateSpeedup"
          ? "candidateSpeedups"
          : moment.kind === "candidateBodybag"
            ? "candidateBodybags"
          : moment.kind === "possiblePopup"
            ? "possiblePopups"
          : moment.kind === "paddleReadiness" || moment.kind === "dinkReadiness"
            ? "paddleReadiness"
            : "leftSideCoverage";
      const overlay = overlayByShotNumber.get(moment.shotNumber) ?? null;
      return {
        key: `${lens}-${moment.key}`,
        lens,
        rallyNumber: moment.rallyIndex + 1,
        shotNumber: moment.shotNumber,
        timeSec: Math.max(0, (moment.contactTimestamp - segmentStartMs) / 1000),
        loopStartSec: Math.max(0, (moment.clipStartMs - segmentStartMs) / 1000),
        loopEndSec: Math.max(0.5, (moment.clipEndMs - segmentStartMs) / 1000),
        title: coachMomentLensTitle(moment),
        detail: coachMomentLensDetail(moment),
        reviewedPlayerId: moment.reviewedPlayerId,
        hitterPlayerId: moment.hitterPlayerId,
        partnerPlayerId: moment.partnerPlayerId,
        reviewedTeam: moment.reviewedTeam,
        rallyWinnerTeamId: moment.rallyWinnerTeamId,
        partnerSideDistanceFt: moment.coveragePartnerSideDistanceFt,
        shotPath: coachMomentLensShotPath(overlay),
        shotPaths: rowShotPaths,
        courtPlayers: coachMomentLensCourtPlayers(
          overlay,
          moment,
          args.insightsToCvPlayerIndex,
        ),
      };
    })
    .sort((a, b) => a.timeSec - b.timeSec);
}

function momentLoopRegion(
  moment: CoachViewMoment | null,
  row: KitchenArrivalRow | null,
  nonce: number,
) {
  if (!moment || !row) return undefined;
  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  return {
    enabled: true,
    aSec: Math.max(0, (moment.clipStartMs - segmentStartMs) / 1000),
    bSec: Math.max(0.5, (moment.clipEndMs - segmentStartMs) / 1000),
    nonce,
  };
}

function momentSeedTimeSec(
  moment: CoachViewMoment | null,
  row: KitchenArrivalRow | null,
): number {
  if (!moment || !row) return 0;
  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  const seedMs =
    moment.kind === "candidateSpeedup" || moment.kind === "candidateBodybag" || moment.kind === "possiblePopup"
      ? moment.clipStartMs
      : moment.contactTimestamp;
  return Math.max(0, (seedMs - segmentStartMs) / 1000);
}

type CoverageBroadSortMode = "partnerDistanceDesc" | "rallyAsc" | "rallyDesc";

function sortCoverageBroadMoments(
  moments: CoachViewMoment[],
  mode: CoverageBroadSortMode,
): CoachViewMoment[] {
  return [...moments].sort((a, b) => {
    if (mode === "rallyAsc" || mode === "rallyDesc") {
      const rallyDelta =
        a.rallyIndex - b.rallyIndex ||
        a.shotNumber - b.shotNumber ||
        (a.coveragePartnerSideDistanceFt ?? -1) -
          (b.coveragePartnerSideDistanceFt ?? -1);
      return mode === "rallyAsc" ? rallyDelta : -rallyDelta;
    }

    const distanceDelta =
      (b.coveragePartnerSideDistanceFt ?? Number.NEGATIVE_INFINITY) -
      (a.coveragePartnerSideDistanceFt ?? Number.NEGATIVE_INFINITY);
    return (
      distanceDelta ||
      a.rallyIndex - b.rallyIndex ||
      a.shotNumber - b.shotNumber
    );
  });
}

function CoverageOutcomeReviewPane(props: {
  title: string;
  emptyText: string;
  moments: CoachViewMoment[];
  allMoments: CoachViewMoment[];
  rows: KitchenArrivalRow[];
  packageId: string;
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  cvJson?: CvJson | null;
  activeColor: string;
  dense?: boolean;
  playerId?: number | null;
}) {
  const tileVideoHeight = props.dense ? 218 : 210;

  return (
    <Box
      sx={{
        minWidth: 0,
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${props.activeColor}`,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "rgba(255,255,255,0.045)",
        boxShadow: `inset 0 3px 0 ${props.activeColor}, 0 0 0 1px rgba(0,0,0,0.32)`,
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.8,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Typography sx={{ fontWeight: 950, color: "white", fontSize: 15 }}>
          {props.title} ({props.moments.length}{" "}
          {props.moments.length === 1 ? "time" : "times"})
        </Typography>
        {typeof props.playerId === "number" ? (
          <PlayerMini
            playerId={props.playerId}
            renderPlayerIdentity={props.renderPlayerIdentity}
            insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
            size={42}
            fontSize={13}
            fontColor="white"
          />
        ) : null}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 0.75,
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          alignContent: "start",
          gap: 0.75,
        }}
      >
        {props.moments.length ? (
          props.moments.map((moment, index) => {
            const row =
              props.rows.find((r) => r.rallyIndex === moment.rallyIndex) ??
              props.rows[moment.rallyIndex] ??
              null;
            const lensMoments = buildActiveRallyLensMomentsForRow({
              row,
              moments: props.allMoments,
              insightsToCvPlayerIndex: props.insightsToCvPlayerIndex,
            });
            const seedNonce = index + 1;

            return (
              <Box
                key={moment.key}
                sx={{
                  minWidth: 0,
                  height: tileVideoHeight + 56,
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 1.25,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "rgba(0,0,0,0.42)",
                }}
              >
                <Box
                  sx={{
                    flex: "0 0 auto",
                    px: 0.8,
                    py: 0.55,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.55, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: 12,
                        fontWeight: 950,
                        whiteSpace: "nowrap",
                      }}
                    >
                      R{moment.rallyIndex + 1} S{moment.shotNumber}
                    </Typography>
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: 12,
                        fontWeight: 950,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatFeet(moment.coveragePartnerSideDistanceFt)}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    position: "relative",
                    bgcolor: "#000",
                    "& .active-rally-video-root": {
                      height: "100%",
                      minHeight: 0,
                    },
                  }}
                >
                  {row ? (
                    <ActiveRallyVideo
                      insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
                      renderPlayerIdentity={props.renderPlayerIdentity}
                      aiContextLabel={props.title}
                      focusedFilter={null}
                      packageId={props.packageId}
                      row={row}
                      rows={props.rows}
                      activeShotNumber={moment.shotNumber}
                      showCondensedTimeline={false}
                      fontColor="white"
                      controlsPlacement="left"
                      firefightsClips={props.firefightsClips}
                      firefightRallySet={props.firefightRallySet}
                      showRateControls={false}
                      showTitle={false}
                      showScoreEtc={false}
                      showAi={false}
                      aiDrawerMode="split"
                      showLoopScrubber={false}
                      defaultLoopUiOpen={false}
                      showPopoutButton
                      showPreviewPlayPauseButton
                      isInFullPopup={false}
                      rallyLensPanelPlacement="default"
                      focusedPlayerId={moment.reviewedPlayerId}
                      rallyLensMoments={lensMoments}
                      initialRallyLens="off"
                      showRallyLensControls={false}
                      showRallyLensPanel={false}
                      popoutInitialRallyLens="leftSideCoverage"
                      seedPlayback={{
                        timeSec: momentSeedTimeSec(moment, row),
                        paused: true,
                        nonce: seedNonce,
                      }}
                      seedLoopRegion={momentLoopRegion(moment, row, seedNonce)}
                      seekToActiveShotOnChange={false}
                      showShotLabels={false}
                      showTimelineTicks={false}
                      previewMode
                      forceMuted
                    />
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.58)",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      Missing rally video.
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })
        ) : (
          <Box
              sx={{
                p: 2,
                border: "1px dashed rgba(255,255,255,0.18)",
                borderRadius: 1,
                color: "rgba(255,255,255,0.62)",
                fontSize: 12,
                gridColumn: "1 / -1",
              }}
            >
            {props.emptyText}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function CoveragePlayerBroadReviewPopup(props: {
  open: boolean;
  playerId: number | null;
  moments: CoachViewMoment[];
  allMoments: CoachViewMoment[];
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const [sortMode, setSortMode] =
    React.useState<CoverageBroadSortMode>("partnerDistanceDesc");
  const playerMoments = React.useMemo(
    () =>
      typeof props.playerId === "number"
        ? props.moments.filter((moment) => moment.reviewedPlayerId === props.playerId)
        : [],
    [props.moments, props.playerId],
  );
  const tookMoments = React.useMemo(
    () =>
      sortCoverageBroadMoments(
        playerMoments.filter(
          (moment) => moment.coverageOutcome === "left_side_took",
        ),
        sortMode,
      ),
    [playerMoments, sortMode],
  );
  const didNotTakeMoments = React.useMemo(
    () =>
      sortCoverageBroadMoments(
        playerMoments.filter(
          (moment) => moment.coverageOutcome === "left_side_did_not_take",
        ),
        sortMode,
      ),
    [playerMoments, sortMode],
  );

  if (!props.open || typeof props.playerId !== "number") return null;

  return (
    <Paper
      data-demo="left-side-broad-review-dialog"
      elevation={14}
      role="dialog"
      aria-modal="false"
      sx={{
        position: "fixed",
        left: 36,
        top: 74,
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 112px)",
        zIndex: 2650,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "rgba(10,14,18,0.96)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Typography sx={{ color: "white", fontSize: 18, fontWeight: 950 }}>
            Left-side broad review
          </Typography>
          <PlayerMini
            playerId={props.playerId}
            renderPlayerIdentity={props.renderPlayerIdentity}
            insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
            size={42}
            fontSize={14}
            fontColor="white"
          />
          <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: 12 }}>
            {playerMoments.length} current-filter moments
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Button
            size="small"
            variant={sortMode === "partnerDistanceDesc" ? "contained" : "outlined"}
            onClick={() => setSortMode("partnerDistanceDesc")}
            sx={{
              height: 30,
              textTransform: "none",
              fontSize: 12,
              fontWeight: 900,
              color: sortMode === "partnerDistanceDesc" ? "white" : "rgba(255,255,255,0.88)",
              borderColor: "rgba(255,255,255,0.32)",
            }}
          >
            Sort by Partner ft
          </Button>
          <Button
            size="small"
            variant={sortMode === "rallyAsc" || sortMode === "rallyDesc" ? "contained" : "outlined"}
            onClick={() =>
              setSortMode((current) =>
                current === "rallyAsc" ? "rallyDesc" : "rallyAsc",
              )
            }
            sx={{
              height: 30,
              textTransform: "none",
              fontSize: 12,
              fontWeight: 900,
              color:
                sortMode === "rallyAsc" || sortMode === "rallyDesc"
                  ? "white"
                  : "rgba(255,255,255,0.88)",
              borderColor: "rgba(255,255,255,0.32)",
            }}
          >
            Sort by Rally
          </Button>
          <Button
            size="small"
            onClick={props.onClose}
            startIcon={<CloseIcon />}
            sx={{ textTransform: "none", fontWeight: 900, color: "white" }}
          >
            Close
          </Button>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, p: 1, display: "flex", gap: 1 }}>
        <CoverageOutcomeReviewPane
          title="Took it"
          emptyText="No took-it moments with the current filters."
          moments={tookMoments}
          allMoments={props.allMoments}
          rows={props.rows}
          packageId={props.packageId}
          insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
          renderPlayerIdentity={props.renderPlayerIdentity}
          firefightsClips={props.firefightsClips}
          firefightRallySet={props.firefightRallySet}
          activeColor="rgba(46,125,50,0.86)"
          dense={tookMoments.length > 12}
          playerId={props.playerId}
        />
        <Box
          aria-hidden="true"
          sx={{
            flex: "0 0 4px",
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.24)",
            boxShadow: "0 0 20px rgba(128,216,255,0.24)",
          }}
        />
        <CoverageOutcomeReviewPane
          title="Did not take it"
          emptyText="No did-not-take moments with the current filters."
          moments={didNotTakeMoments}
          allMoments={props.allMoments}
          rows={props.rows}
          packageId={props.packageId}
          insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
          renderPlayerIdentity={props.renderPlayerIdentity}
          firefightsClips={props.firefightsClips}
          firefightRallySet={props.firefightRallySet}
          activeColor="rgba(25,118,210,0.86)"
          dense={didNotTakeMoments.length > 12}
          playerId={props.playerId}
        />
      </Box>
    </Paper>
  );
}

function PaddleReadinessBroadReviewCard(props: {
  kind: CoachViewMomentKind;
  moment: CoachViewMoment;
  index: number;
  cardWidth: number;
  row: KitchenArrivalRow | null;
  lensMoments: ActiveRallyLensMoment[];
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  cvJson?: CvJson | null;
}) {
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const [hasEnteredView, setHasEnteredView] = React.useState(false);

  React.useEffect(() => {
    if (hasEnteredView) return;
    const node = cardRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setHasEnteredView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasEnteredView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasEnteredView]);

  const seedNonce = props.index + 1;
  const isCandidateSpeedup = props.kind === "candidateSpeedup";
  const isCandidateBodybag = props.kind === "candidateBodybag";
  const isPossiblePopup = props.kind === "possiblePopup";
  const isCandidateAttack = isCandidateSpeedup || isCandidateBodybag;
  const cardContextLabel = isCandidateSpeedup
    ? "Possible speedup review"
    : isCandidateBodybag
      ? "Possible jammed-contact review"
    : isPossiblePopup
      ? "Possible popup review"
    : props.kind === "dinkReadiness"
      ? "Dink focus broad review"
      : "Paddle readiness broad review";
  const headerDetail = isPossiblePopup
    ? props.moment.gameStageLabel
    : isCandidateAttack
    ? `Estimated ${speedupLaneLabel(props.moment.speedupLane).toLowerCase()} · ${incomingBounceLabel(
        props.moment.incomingBounceKind,
      )}`
    : `Responding to: ${shotTypeLabel(props.moment.shotType)} · ${props.moment.gameStageLabel}`;
  const popoutLens: ActiveRallyLensMoment["lens"] = isCandidateAttack
    ? isCandidateBodybag
      ? "candidateBodybags"
      : "candidateSpeedups"
    : isPossiblePopup
      ? "possiblePopups"
      : "paddleReadiness";

  return (
    <Box
      ref={cardRef}
      data-demo={isPossiblePopup ? "possible-popup-card" : undefined}
      data-rally-number={props.moment.rallyIndex + 1}
      data-shot-number={props.moment.shotNumber}
      data-player-id={props.moment.reviewedPlayerId}
      sx={{
        width: props.cardWidth,
        maxWidth: "calc(100vw - 112px)",
        minWidth: 0,
        flex: "0 0 auto",
        height: "100%",
        border:
          props.index === 0 && !isPossiblePopup
            ? "2px solid rgba(128,216,255,0.84)"
            : "1px solid rgba(128,216,255,0.28)",
        borderRadius: 1.5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "rgba(0,0,0,0.46)",
        boxShadow:
          props.index === 0 && !isPossiblePopup
            ? "0 0 0 3px rgba(41,182,246,0.20), inset 0 3px 0 rgba(25,118,210,0.9)"
            : "inset 0 3px 0 rgba(25,118,210,0.56)",
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          px: 0.9,
          py: 0.65,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontSize: 12,
            fontWeight: 950,
            whiteSpace: "nowrap",
          }}
        >
          R{props.moment.rallyIndex + 1} S{props.moment.shotNumber}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.72)",
            fontSize: 12,
            fontWeight: 900,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {headerDetail}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          position: "relative",
          bgcolor: "#000",
          "& .active-rally-video-root": {
            height: "100%",
            minHeight: 0,
          },
          "& video": {
            width: "100% !important",
            height: "100% !important",
            objectFit: "cover !important",
            objectPosition: "center 42%",
            transform: isPossiblePopup ? "none" : "scale(1.5)",
            transformOrigin: "center 42%",
          },
        }}
      >
        {props.row && hasEnteredView ? (
          <>
            <ActiveRallyVideo
              insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
              renderPlayerIdentity={props.renderPlayerIdentity}
              aiContextLabel={cardContextLabel}
              focusedFilter={null}
              packageId={props.packageId}
              row={props.row}
              rows={props.rows}
              activeShotNumber={props.moment.shotNumber}
              showCondensedTimeline={false}
              fontColor="white"
              controlsPlacement="left"
              firefightsClips={props.firefightsClips}
              firefightRallySet={props.firefightRallySet}
              showRateControls={isPossiblePopup}
              defaultPlaybackRate={isPossiblePopup ? 0.25 : 1}
              highlightActiveShotTrajectory={isPossiblePopup}
              cvJson={props.cvJson}
              showTitle={false}
              showScoreEtc={false}
              showAi={false}
              aiDrawerMode="split"
              showLoopScrubber={false}
              defaultLoopUiOpen={false}
              showPopoutButton
              showPreviewPlayPauseButton
              isInFullPopup={false}
              rallyLensPanelPlacement="default"
              focusedPlayerId={props.moment.reviewedPlayerId}
              rallyLensMoments={props.lensMoments}
              initialRallyLens="off"
              showRallyLensControls={false}
              showRallyLensPanel={false}
              popoutInitialRallyLens={popoutLens}
              initialRallyLensMomentKey={activeRallyLensKeyForCoachMoment(props.moment)}
              seedPlayback={{
                timeSec: momentSeedTimeSec(props.moment, props.row),
                paused: true,
                nonce: seedNonce,
              }}
              seedLoopRegion={momentLoopRegion(props.moment, props.row, seedNonce)}
              seekToActiveShotOnChange={false}
              showShotLabels={false}
              showTimelineTicks={false}
              previewMode
              forceMuted
            />
            {isPossiblePopup && props.moment.candidateReason ? (
              <Box
                data-demo="possible-popup-reason-overlay"
                sx={{
                  position: "absolute",
                  left: 10,
                  right: 48,
                  bottom: 10,
                  zIndex: 8,
                  px: 1,
                  py: 0.75,
                  borderLeft: "3px solid rgba(255,193,7,0.96)",
                  bgcolor: "rgba(10,12,14,0.88)",
                  boxShadow: "0 3px 14px rgba(0,0,0,0.45)",
                  pointerEvents: "none",
                }}
              >
                <Typography sx={{ color: "rgba(255,213,79,0.98)", fontSize: 10, fontWeight: 950 }}>
                  WHY FLAGGED
                </Typography>
                <Typography
                  sx={{
                    mt: 0.15,
                    color: "rgba(255,255,255,0.94)",
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.25,
                  }}
                >
                  {props.moment.candidateReason}
                </Typography>
              </Box>
            ) : null}
          </>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.58)",
              fontSize: 13,
              fontWeight: 850,
            }}
          >
            {props.row ? "Scroll into view to load video." : "Missing rally video."}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function paddleReadinessCardWidth(previewScale: 1 | 2 | 4 | 8): number {
  return previewScale === 1
    ? 880
    : previewScale === 2
      ? 620
      : previewScale === 4
        ? 430
        : 300;
}

function PaddleReadinessScaleControls(props: {
  value: 1 | 2 | 4 | 8;
  onChange: (value: 1 | 2 | 4 | 8) => void;
  dark?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.35,
        p: 0.25,
        border: props.dark
          ? "1px solid rgba(255,255,255,0.16)"
          : "1px solid rgba(25,118,210,0.20)",
        borderRadius: 1.25,
        bgcolor: props.dark ? "rgba(255,255,255,0.04)" : "rgba(25,118,210,0.045)",
      }}
    >
      {([
        [1, "Full"],
        [2, "1/2"],
        [4, "1/4"],
        [8, "1/8"],
      ] as const).map(([value, label]) => (
        <Button
          key={value}
          data-demo={`review-scale-${value}`}
          size="small"
          variant={props.value === value ? "contained" : "text"}
          onClick={() => props.onChange(value)}
          sx={{
            minWidth: 42,
            height: 24,
            px: 0.75,
            py: 0,
            textTransform: "none",
            fontSize: 11,
            fontWeight: 950,
            color: props.value === value
              ? "white"
              : props.dark
                ? "rgba(255,255,255,0.78)"
                : "#1976d2",
          }}
        >
          {label}
        </Button>
      ))}
    </Box>
  );
}

function PaddleReadinessCarousel(props: {
  kind: CoachViewMomentKind;
  moments: CoachViewMoment[];
  allMoments: CoachViewMoment[];
  emptyMessage?: string;
  previewScale: 1 | 2 | 4 | 8;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  cvJson?: CvJson | null;
}) {
  const cardWidth = paddleReadinessCardWidth(props.previewScale);
  return (
    <Box
      data-demo="paddle-readiness-carousel"
      sx={{
        flex: 1,
        minHeight: 0,
        overflowX: "auto",
        overflowY: "hidden",
        p: 1.25,
        display: "grid",
        gridAutoFlow: "column",
        gridTemplateRows: `repeat(${props.previewScale}, minmax(0, 1fr))`,
        gridAutoColumns: `${cardWidth}px`,
        alignItems: "stretch",
        gap: 1.25,
        bgcolor: "rgba(8,12,16,0.96)",
      }}
    >
      {props.moments.length ? (
        props.moments.map((moment, index) => {
          const row =
            props.rows.find((r) => r.rallyIndex === moment.rallyIndex) ??
            props.rows[moment.rallyIndex] ??
            null;
          const lensMoments = buildActiveRallyLensMomentsForRow({
            row,
            moments:
              props.kind === "candidateSpeedup" ||
              props.kind === "candidateBodybag" ||
              props.kind === "possiblePopup"
                ? [moment]
                : props.allMoments,
            insightsToCvPlayerIndex: props.insightsToCvPlayerIndex,
          });
          return (
            <PaddleReadinessBroadReviewCard
              key={moment.key}
              kind={props.kind}
              moment={moment}
              index={index}
              cardWidth={cardWidth}
              row={row}
              lensMoments={lensMoments}
              packageId={props.packageId}
              rows={props.rows}
              insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
              renderPlayerIdentity={props.renderPlayerIdentity}
              firefightsClips={props.firefightsClips}
              firefightRallySet={props.firefightRallySet}
              cvJson={props.cvJson}
            />
          );
        })
      ) : (
        <Box
          sx={{
            p: 2,
            border: "1px dashed rgba(255,255,255,0.18)",
            borderRadius: 1,
            color: "rgba(255,255,255,0.62)",
            fontSize: 12,
            gridColumn: "1 / -1",
          }}
        >
          {props.emptyMessage ?? "No moments with the current filters."}
        </Box>
      )}
    </Box>
  );
}

function PaddlePlayerBroadReviewPopup(props: {
  open: boolean;
  kind: CoachViewMomentKind;
  title: string;
  playerId: number | null;
  moments: CoachViewMoment[];
  allMoments: CoachViewMoment[];
  emptyMessage?: string;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  cvJson?: CvJson | null;
}) {
  const [previewScale, setPreviewScale] = React.useState<1 | 2 | 4 | 8>(1);
  const playerMoments = React.useMemo(
    () =>
      typeof props.playerId === "number"
        ? props.moments.filter((moment) => moment.reviewedPlayerId === props.playerId)
        : [],
    [props.moments, props.playerId],
  );
  const previewRows = previewScale;

  if (!props.open || typeof props.playerId !== "number") return null;

  return (
    <Paper
      elevation={14}
      role="dialog"
      aria-modal="false"
      sx={{
        position: "fixed",
        left: 42,
        top: 78,
        width: "calc(100vw - 84px)",
        height: "calc(100vh - 124px)",
        zIndex: 2650,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "rgba(10,14,18,0.96)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Typography sx={{ color: "white", fontSize: 18, fontWeight: 950 }}>
            {props.title}
          </Typography>
          <PlayerMini
            playerId={props.playerId}
            renderPlayerIdentity={props.renderPlayerIdentity}
            insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
            size={42}
            fontSize={14}
            fontColor="white"
          />
          <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: 12 }}>
            {playerMoments.length} current-filter moments
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <PaddleReadinessScaleControls
            value={previewScale}
            onChange={setPreviewScale}
            dark
          />
          <Button
            size="small"
            onClick={props.onClose}
            startIcon={<CloseIcon />}
            sx={{ textTransform: "none", fontWeight: 900, color: "white" }}
          >
            Close
          </Button>
        </Box>
      </Box>
      <PaddleReadinessCarousel
        kind={props.kind}
        moments={playerMoments}
        allMoments={props.allMoments}
        emptyMessage={props.emptyMessage}
        previewScale={previewRows}
        packageId={props.packageId}
        rows={props.rows}
        insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
        renderPlayerIdentity={props.renderPlayerIdentity}
        firefightsClips={props.firefightsClips}
        firefightRallySet={props.firefightRallySet}
      />
    </Paper>
  );
}

export function CoachMomentGalleryPopup(props: {
  open: boolean;
  title: string;
  subtitle: string;
  kind: CoachViewMomentKind;
  icon?: React.ReactNode;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  moments: CoachViewMoment[];
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
  playerLabel?: (playerId: number) => string;
  teamLabel?: (team: 0 | 1) => string;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  cvJson?: CvJson | null;
}) {
  const {
    open,
    onClose,
    moments,
    rows,
    packageId,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
    firefightsClips,
    firefightRallySet,
  } = props;

  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const [reviewedPlayerFilter, setReviewedPlayerFilter] = React.useState("all");
  const [hitterFilter, setHitterFilter] = React.useState("all");
  const [hitterTeamFilter, setHitterTeamFilter] = React.useState("all");
  const [winnerFilter, setWinnerFilter] = React.useState("all");
  const [shotTypeFilter, setShotTypeFilter] = React.useState("all");
  const [gameStageFilter, setGameStageFilter] = React.useState("all");
  const [partnerFilter, setPartnerFilter] = React.useState("all");
  const [coverageKindFilter, setCoverageKindFilter] = React.useState("all");
  const [coverageOutcomeFilter, setCoverageOutcomeFilter] = React.useState("all");
  const [incomingBounceFilter, setIncomingBounceFilter] = React.useState("all");
  const [speedupLaneFilter, setSpeedupLaneFilter] = React.useState("all");
  const [possiblePopupCourtStateFilter, setPossiblePopupCourtStateFilter] =
    React.useState("all");
  const [partnerSideSort, setPartnerSideSort] = React.useState<
    "none" | "desc" | "asc"
  >("none");
  const [coverageMainSortMode, setCoverageMainSortMode] =
    React.useState<CoverageBroadSortMode>("partnerDistanceDesc");
  const [coverageBroadReviewPlayerId, setCoverageBroadReviewPlayerId] =
    React.useState<number | null>(null);
  const [paddleBroadReviewPlayerId, setPaddleBroadReviewPlayerId] =
    React.useState<number | null>(null);
  const [showPaddleMoreFilters, setShowPaddleMoreFilters] = React.useState(false);
  const [paddlePreviewScale, setPaddlePreviewScale] =
    React.useState<1 | 2 | 4 | 8>(1);
  const isReadinessKind =
    props.kind === "paddleReadiness" || props.kind === "dinkReadiness";
  const isCandidateAttackKind =
    props.kind === "candidateSpeedup" || props.kind === "candidateBodybag";
  const isCarouselKind =
    isReadinessKind || isCandidateAttackKind || props.kind === "possiblePopup";

  const defaultGameStageFilter = React.useMemo(
    () => defaultGameStageFilterForKind(props.kind, moments),
    [props.kind, moments],
  );
  const defaultShotTypeFilter = React.useMemo(
    () => defaultShotTypeFilterForKind(props.kind, moments),
    [props.kind, moments],
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedKey(moments[0]?.key ?? null);
    setGameStageFilter(defaultGameStageFilter);
    setShotTypeFilter(defaultShotTypeFilter);
    setPossiblePopupCourtStateFilter("all");
  }, [open, moments, defaultGameStageFilter, defaultShotTypeFilter]);

  const filtered = React.useMemo(() => {
    return moments.filter((moment) => {
      if (
        reviewedPlayerFilter !== "all" &&
        String(
          isCandidateAttackKind
            ? moment.hitterPlayerId
            : moment.reviewedPlayerId,
        ) !== reviewedPlayerFilter
      ) return false;
      if (hitterFilter !== "all" && String(moment.hitterPlayerId) !== hitterFilter) return false;
      if (hitterTeamFilter !== "all" && String(moment.hitterTeam) !== hitterTeamFilter) return false;
      if (winnerFilter !== "all" && String(moment.rallyWinnerTeamId) !== winnerFilter) return false;
      if (shotTypeFilter !== "all" && moment.shotType !== shotTypeFilter) return false;
      if (gameStageFilter !== "all" && moment.gameStageKey !== gameStageFilter) return false;
      if (partnerFilter !== "all" && String(moment.partnerPlayerId) !== partnerFilter) return false;
      if (coverageKindFilter !== "all" && moment.coverageCandidateKind !== coverageKindFilter) return false;
      if (coverageOutcomeFilter !== "all" && moment.coverageOutcome !== coverageOutcomeFilter) return false;
      if (incomingBounceFilter !== "all" && moment.incomingBounceKind !== incomingBounceFilter) return false;
      if (speedupLaneFilter !== "all" && moment.speedupLane !== speedupLaneFilter) return false;
      if (
        possiblePopupCourtStateFilter !== "all" &&
        moment.possiblePopupCourtState !== possiblePopupCourtStateFilter
      ) return false;
      return true;
    });
  }, [
    moments,
    isCandidateAttackKind,
    props.kind,
    reviewedPlayerFilter,
    hitterFilter,
    hitterTeamFilter,
    winnerFilter,
    shotTypeFilter,
    gameStageFilter,
    partnerFilter,
    coverageKindFilter,
    coverageOutcomeFilter,
    incomingBounceFilter,
    speedupLaneFilter,
    possiblePopupCourtStateFilter,
  ]);

  const sortedFiltered = React.useMemo(() => {
    if (props.kind !== "leftSideCoverage" || partnerSideSort === "none") {
      return filtered;
    }
    return [...filtered].sort((a, b) => {
      const av =
        typeof a.coveragePartnerSideDistanceFt === "number"
          ? a.coveragePartnerSideDistanceFt
          : Number.NEGATIVE_INFINITY;
      const bv =
        typeof b.coveragePartnerSideDistanceFt === "number"
          ? b.coveragePartnerSideDistanceFt
          : Number.NEGATIVE_INFINITY;
      return partnerSideSort === "desc" ? bv - av : av - bv;
    });
  }, [filtered, partnerSideSort, props.kind]);

  const leftSidePlayerReachStats = React.useMemo(() => {
    const stats = new Map<
      number,
      {
        took: number;
        total: number;
        tookDistanceCount: number;
        tookDistanceSum: number;
        maxTookDistanceFt: number | null;
        maxCandidateDistanceFt: number | null;
      }
    >();
    if (props.kind !== "leftSideCoverage") return stats;

    for (const moment of moments) {
      if (hitterFilter !== "all" && String(moment.hitterPlayerId) !== hitterFilter) continue;
      if (hitterTeamFilter !== "all" && String(moment.hitterTeam) !== hitterTeamFilter) continue;
      if (winnerFilter !== "all" && String(moment.rallyWinnerTeamId) !== winnerFilter) continue;
      if (shotTypeFilter !== "all" && moment.shotType !== shotTypeFilter) continue;
      if (gameStageFilter !== "all" && moment.gameStageKey !== gameStageFilter) continue;
      if (incomingBounceFilter !== "all" && moment.incomingBounceKind !== incomingBounceFilter) continue;
      if (speedupLaneFilter !== "all" && moment.speedupLane !== speedupLaneFilter) continue;

      const current = stats.get(moment.reviewedPlayerId) ?? {
        took: 0,
        total: 0,
        tookDistanceCount: 0,
        tookDistanceSum: 0,
        maxTookDistanceFt: null,
        maxCandidateDistanceFt: null,
      };
      current.total += 1;
      const distance =
        typeof moment.coveragePartnerSideDistanceFt === "number" &&
        Number.isFinite(moment.coveragePartnerSideDistanceFt)
          ? moment.coveragePartnerSideDistanceFt
          : null;
      if (distance != null) {
        current.maxCandidateDistanceFt =
          current.maxCandidateDistanceFt == null
            ? distance
            : Math.max(current.maxCandidateDistanceFt, distance);
      }
      if (moment.coverageOutcome === "left_side_took") {
        current.took += 1;
        if (distance != null) {
          current.tookDistanceCount += 1;
          current.tookDistanceSum += distance;
          current.maxTookDistanceFt =
            current.maxTookDistanceFt == null
              ? distance
              : Math.max(current.maxTookDistanceFt, distance);
        }
      }
      stats.set(moment.reviewedPlayerId, current);
    }

    return stats;
  }, [
    props.kind,
    moments,
    hitterFilter,
    hitterTeamFilter,
    winnerFilter,
    shotTypeFilter,
    gameStageFilter,
    incomingBounceFilter,
    speedupLaneFilter,
  ]);

  const coverageBroadReviewMoments = React.useMemo(() => {
    if (props.kind !== "leftSideCoverage") return [];
    return moments.filter((moment) => {
      if (hitterFilter !== "all" && String(moment.hitterPlayerId) !== hitterFilter) return false;
      if (hitterTeamFilter !== "all" && String(moment.hitterTeam) !== hitterTeamFilter) return false;
      if (winnerFilter !== "all" && String(moment.rallyWinnerTeamId) !== winnerFilter) return false;
      if (shotTypeFilter !== "all" && moment.shotType !== shotTypeFilter) return false;
      if (gameStageFilter !== "all" && moment.gameStageKey !== gameStageFilter) return false;
      if (incomingBounceFilter !== "all" && moment.incomingBounceKind !== incomingBounceFilter) return false;
      if (speedupLaneFilter !== "all" && moment.speedupLane !== speedupLaneFilter) return false;
      if (
        possiblePopupCourtStateFilter !== "all" &&
        moment.possiblePopupCourtState !== possiblePopupCourtStateFilter
      ) return false;
      return true;
    });
  }, [
    props.kind,
    moments,
    hitterFilter,
    hitterTeamFilter,
    winnerFilter,
    shotTypeFilter,
    gameStageFilter,
    incomingBounceFilter,
    speedupLaneFilter,
    possiblePopupCourtStateFilter,
  ]);

  const coverageCarouselPlayerId = React.useMemo(() => {
    if (props.kind !== "leftSideCoverage" || reviewedPlayerFilter === "all") {
      return null;
    }
    const playerId = Number(reviewedPlayerFilter);
    return Number.isFinite(playerId) ? playerId : null;
  }, [props.kind, reviewedPlayerFilter]);

  const coverageTookMoments = React.useMemo(() => {
    if (props.kind !== "leftSideCoverage") return [];
    return sortCoverageBroadMoments(
      sortedFiltered.filter(
        (moment) => moment.coverageOutcome === "left_side_took",
      ),
      coverageMainSortMode,
    );
  }, [props.kind, sortedFiltered, coverageMainSortMode]);

  const coverageDidNotTakeMoments = React.useMemo(() => {
    if (props.kind !== "leftSideCoverage") return [];
    return sortCoverageBroadMoments(
      sortedFiltered.filter(
        (moment) => moment.coverageOutcome === "left_side_did_not_take",
      ),
      coverageMainSortMode,
    );
  }, [props.kind, sortedFiltered, coverageMainSortMode]);

  const paddlePlayerReviewMoments = React.useMemo(() => {
    if (!isCarouselKind) return [];
    return moments.filter((moment) => {
      if (hitterFilter !== "all" && String(moment.hitterPlayerId) !== hitterFilter) return false;
      if (hitterTeamFilter !== "all" && String(moment.hitterTeam) !== hitterTeamFilter) return false;
      if (winnerFilter !== "all" && String(moment.rallyWinnerTeamId) !== winnerFilter) return false;
      if (shotTypeFilter !== "all" && moment.shotType !== shotTypeFilter) return false;
      if (gameStageFilter !== "all" && moment.gameStageKey !== gameStageFilter) return false;
      if (incomingBounceFilter !== "all" && moment.incomingBounceKind !== incomingBounceFilter) return false;
      if (speedupLaneFilter !== "all" && moment.speedupLane !== speedupLaneFilter) return false;
      return true;
    });
  }, [
    isReadinessKind,
    isCarouselKind,
    moments,
    hitterFilter,
    hitterTeamFilter,
    winnerFilter,
    shotTypeFilter,
    gameStageFilter,
    incomingBounceFilter,
    speedupLaneFilter,
  ]);

  const paddlePlayerCounts = React.useMemo(() => {
    const counts = new Map<number, number>();
    if (!isCarouselKind) return counts;
    for (const moment of paddlePlayerReviewMoments) {
      const playerId =
        isCandidateAttackKind
          ? moment.hitterPlayerId
          : moment.reviewedPlayerId;
      counts.set(
        playerId,
        (counts.get(playerId) ?? 0) + 1,
      );
    }
    return counts;
  }, [isCarouselKind, paddlePlayerReviewMoments, props.kind]);

  const paddlePlayerTotalCounts = React.useMemo(() => {
    const counts = new Map<number, number>();
    if (!isCarouselKind) return counts;
    for (const moment of moments) {
      const playerId =
        isCandidateAttackKind
          ? moment.hitterPlayerId
          : moment.reviewedPlayerId;
      counts.set(playerId, (counts.get(playerId) ?? 0) + 1);
    }
    return counts;
  }, [isCarouselKind, moments, props.kind]);

  const candidateSpeedupPlayerOutcomes = React.useMemo(() => {
    const counts = new Map<number, { won: number; lost: number; unknown: number }>();
    if (!isCandidateAttackKind) return counts;
    for (const moment of paddlePlayerReviewMoments) {
      const playerId = moment.hitterPlayerId;
      const current = counts.get(playerId) ?? { won: 0, lost: 0, unknown: 0 };
      if (moment.rallyWinnerTeamId !== 0 && moment.rallyWinnerTeamId !== 1) {
        current.unknown += 1;
      } else if (moment.hitterTeam === moment.rallyWinnerTeamId) {
        current.won += 1;
      } else {
        current.lost += 1;
      }
      counts.set(playerId, current);
    }
    return counts;
  }, [isCandidateAttackKind, paddlePlayerReviewMoments]);

  React.useEffect(() => {
    if (!open) return;
    if (selectedKey && sortedFiltered.some((moment) => moment.key === selectedKey)) return;
    setSelectedKey(sortedFiltered[0]?.key ?? null);
  }, [open, sortedFiltered, selectedKey]);

  const selected = sortedFiltered.find((moment) => moment.key === selectedKey) ?? sortedFiltered[0] ?? null;
  const selectedRow = selected
    ? rows.find((row) => row.rallyIndex === selected.rallyIndex) ??
      rows[selected.rallyIndex] ??
      null
    : null;

  const [seedNonce, setSeedNonce] = React.useState(0);
  React.useEffect(() => {
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [selected?.key]);

  const selectedLoopRegion = React.useMemo(() => {
    if (!selected || !selectedRow) return undefined;
    const segmentStartMs = selectedRow.segmentStartMs ?? selectedRow.startMs ?? 0;
    return {
      enabled: true,
      aSec: Math.max(0, (selected.clipStartMs - segmentStartMs) / 1000),
      bSec: Math.max(0.5, (selected.clipEndMs - segmentStartMs) / 1000),
      nonce: seedNonce,
    };
  }, [selected?.key, selectedRow, seedNonce]);

  const seedTimeSec = React.useMemo(() => {
    if (!selected || !selectedRow) return 0;
    const segmentStartMs = selectedRow.segmentStartMs ?? selectedRow.startMs ?? 0;
    return Math.max(0, (selected.contactTimestamp - segmentStartMs) / 1000);
  }, [selected?.key, selectedRow]);

  const playerIds = React.useMemo(
    () =>
      props.kind === "leftSideCoverage"
        ? uniqueNumbers(moments.map((m) => m.reviewedPlayerId))
        : isCandidateAttackKind
          ? uniqueNumbers(moments.map((m) => m.hitterPlayerId))
        : uniqueNumbers([
            ...moments.map((m) => m.reviewedPlayerId),
            ...moments.map((m) => m.hitterPlayerId),
          ]),
    [isCandidateAttackKind, props.kind, moments],
  );
  const shotTypes = React.useMemo(() => uniqueStrings(moments.map((m) => m.shotType)), [moments]);
  const coverageOutcomes = React.useMemo(
    () => uniqueStrings(moments.map((m) => m.coverageOutcome)),
    [moments],
  );
  const incomingBounceKinds = React.useMemo(
    () => uniqueStrings(moments.map((m) => m.incomingBounceKind)),
    [moments],
  );
  const speedupLanes = React.useMemo(
    () => uniqueStrings(moments.map((m) => m.speedupLane)),
    [moments],
  );
  const gameStages = React.useMemo(() => {
    const byKey = new Map<string, string>();
    for (const moment of moments) {
      byKey.set(moment.gameStageKey, moment.gameStageLabel);
    }
    return Array.from(byKey.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [moments]);

  const handleHitterFilterChange = React.useCallback(
    (value: string) => {
      setHitterFilter(value);
      if (value === "all") return;
      const hitMoment = moments.find(
        (moment) => String(moment.hitterPlayerId) === value,
      );
      if (hitMoment?.hitterTeam === 0 || hitMoment?.hitterTeam === 1) {
        setHitterTeamFilter(String(hitMoment.hitterTeam));
      }
    },
    [moments],
  );

  const selectedRowLensMoments = React.useMemo<ActiveRallyLensMoment[]>(
    () =>
      buildActiveRallyLensMomentsForRow({
        row: selectedRow,
        moments,
        insightsToCvPlayerIndex,
      }),
    [insightsToCvPlayerIndex, moments, selectedRow],
  );
  const selectedRowInitialLens = selectedRowLensMoments[0]?.lens ?? "off";

  if (!open) return null;

  const teamOption = (team: 0 | 1) => props.teamLabel?.(team) ?? `Team ${team}`;
  const tablePanelWidth =
    props.kind === "leftSideCoverage"
      ? 840
      : isReadinessKind
        ? 820
        : 760;
  const tableMinWidth =
    props.kind === "leftSideCoverage"
      ? 890
      : isReadinessKind
        ? 780
        : 740;
  const paddleReadinessPlayerCards =
    isCarouselKind ? (
      <Box
        sx={{
          px: 1.25,
          py: 0.65,
          display: "flex",
          gap: 0.75,
          alignItems: "stretch",
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          bgcolor: "rgba(25,118,210,0.035)",
        }}
      >
        {playerIds.map((playerId) => {
          const displayPlayerLabel =
            packageId === "tito_qtrs_may_30_2026" && playerId === 0
              ? "Tito"
              : props.playerLabel?.(playerId) ?? `p${playerId}`;
          const count = paddlePlayerCounts.get(playerId) ?? 0;
          const total = paddlePlayerTotalCounts.get(playerId) ?? count;
          const speedupOutcome = candidateSpeedupPlayerOutcomes.get(playerId);
          const active = reviewedPlayerFilter === String(playerId);
          const selectPlayer = () => {
            setReviewedPlayerFilter(String(playerId));
            if (isCandidateAttackKind) {
              setHitterFilter("all");
              setHitterTeamFilter("all");
            }
          };
          return (
            <Box
              key={playerId}
              data-player-label={displayPlayerLabel}
              data-player-id={playerId}
              role="button"
              tabIndex={0}
              onClick={selectPlayer}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  selectPlayer();
                }
              }}
              sx={{
                minWidth: 166,
                px: 0.75,
                py: 0.55,
                border: active
                  ? "2px solid rgba(25,118,210,0.9)"
                  : "1px solid rgba(25,118,210,0.25)",
                borderRadius: 1.25,
                bgcolor: active ? "rgba(25,118,210,0.12)" : "rgba(255,255,255,0.78)",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                cursor: "pointer",
              }}
            >
              <PlayerMini
                playerId={playerId}
                renderPlayerIdentity={renderPlayerIdentity}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                size={42}
                fontSize={13}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 950 }}>
                  {displayPlayerLabel}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 950 }}>
                  {isCandidateAttackKind
                    ? `${count}/${total} candidates`
                    : `${total !== count ? `${count}/${total}` : count} ${
                        count === 1 ? "moment" : "moments"
                      }`}
                </Typography>
                {isCandidateAttackKind && speedupOutcome ? (
                  <Typography
                    sx={{
                      mt: 0.1,
                      fontSize: 10.5,
                      fontWeight: 850,
                      color: "rgba(0,0,0,0.68)",
                    }}
                  >
                    Won {speedupOutcome.won} · Lost {speedupOutcome.lost}
                  </Typography>
                ) : null}
                <Typography
                  sx={{
                    mt: 0.2,
                    fontSize: 10.5,
                    fontWeight: 900,
                    color: active ? "#1976d2" : "rgba(0,0,0,0.58)",
                  }}
                >
                  {active ? "Showing" : "Click to show"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    ) : null;

  return (
    <>
    <Paper
      elevation={12}
      role="dialog"
      aria-modal="false"
      data-happy-path={`${props.kind}-popup`}
      sx={{
        position: "fixed",
        left: props.kind === "possiblePopup" ? "50%" : 24,
        transform: props.kind === "possiblePopup" ? "translateX(-50%)" : "none",
        top: 56,
        width: 1440,
        maxWidth: "calc(100vw - 48px)",
        height: "calc(100vh - 84px)",
        zIndex: 2400,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {props.icon}
          <Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 950 }}>
                {props.title}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.65 }}>
                {filtered.length}/{moments.length} moments
              </Typography>
            </Box>
            {props.subtitle ? (
              <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                {props.subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Button
          size="small"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Close
        </Button>
      </Box>

      {paddleReadinessPlayerCards}

      <Box
        sx={{
          px: 1.25,
          py: 0.9,
          display: "flex",
          gap: 1,
          alignItems: "end",
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
        }}
      >
        {!isCarouselKind ? (
          <PlayerFilterChipGroup
            label={props.kind === "leftSideCoverage" ? "Left-side player" : "Reviewed player"}
            value={reviewedPlayerFilter}
            onChange={setReviewedPlayerFilter}
            playerIds={playerIds}
            renderPlayerIdentity={renderPlayerIdentity}
            insightsToCvPlayerIndex={insightsToCvPlayerIndex}
            playerSuffix={
              props.kind === "leftSideCoverage"
                ? (playerId) => {
                    const count = leftSidePlayerReachStats.get(playerId);
                    return count ? `${count.took}/${count.total}` : "0/0";
                  }
                : undefined
            }
          />
        ) : null}
        {isCarouselKind ? (
          <>
            <Box
              sx={{
                height: 30,
                px: 1,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                bgcolor: "rgba(25,118,210,0.09)",
                border: "1px solid rgba(25,118,210,0.28)",
                color: "rgba(0,0,0,0.72)",
                fontSize: 12,
                fontWeight: 900,
              }}
            >
              {props.kind === "candidateSpeedup"
                ? "Kitchen-exchange candidates"
                : props.kind === "candidateBodybag"
                  ? "Body-line candidates"
                : props.kind === "possiblePopup"
                  ? "Possible popups by court shape"
                : props.kind === "dinkReadiness"
                  ? "Kitchen / dink shots"
                  : "Kitchen exchange"}
            </Box>
            {props.kind === "possiblePopup" ? (
              <FilterChipGroup
                label="Court state"
                value={possiblePopupCourtStateFilter}
                onChange={setPossiblePopupCourtStateFilter}
                options={[
                  { value: "all", label: "All" },
                  { value: "all_kitchen", label: "All at kitchen" },
                  { value: "transitioning", label: "One side transitioning" },
                ]}
              />
            ) : null}
            {props.kind === "dinkReadiness" ? (
              <FilterChipGroup
                label="PB Vision kitchen label"
                value={shotTypeFilter}
                onChange={setShotTypeFilter}
                options={[
                  { value: "all", label: "All" },
                  ...shotTypes.map((type) => ({
                    value: type,
                    label: shotTypeLabel(type),
                  })),
                ]}
              />
            ) : null}
            <PaddleReadinessScaleControls
              value={paddlePreviewScale}
              onChange={setPaddlePreviewScale}
            />
            <Button
              size="small"
              variant={showPaddleMoreFilters ? "contained" : "outlined"}
              onClick={() => setShowPaddleMoreFilters((current) => !current)}
              sx={{ textTransform: "none", fontWeight: 900, height: 30 }}
            >
              More filters
            </Button>
          </>
        ) : null}
        {props.kind !== "leftSideCoverage" && !isCarouselKind ? (
          <>
            <PlayerFilterChipGroup
              label="Hitter"
              value={hitterFilter}
              onChange={handleHitterFilterChange}
              playerIds={playerIds}
              renderPlayerIdentity={renderPlayerIdentity}
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
            />
            <FilterChipGroup
              label="Hitter team"
              value={hitterTeamFilter}
              onChange={setHitterTeamFilter}
              options={[
                { value: "all", label: "All" },
                { value: "0", label: teamOption(0) },
                { value: "1", label: teamOption(1) },
              ]}
            />
          </>
        ) : null}
        {!isCarouselKind ? (
          <FilterChipGroup
            label="Rally winner"
            value={winnerFilter}
            onChange={setWinnerFilter}
            options={[
              { value: "all", label: "All" },
              { value: "0", label: teamOption(0) },
              { value: "1", label: teamOption(1) },
            ]}
          />
        ) : null}
        {props.kind !== "leftSideCoverage" && !isCarouselKind ? (
          <FilterChipGroup
            label="Shot type"
            value={shotTypeFilter}
            onChange={setShotTypeFilter}
            options={[
              { value: "all", label: "All" },
              ...shotTypes.map((type) => ({ value: type, label: shotTypeLabel(type) })),
            ]}
          />
        ) : null}
        {props.kind !== "leftSideCoverage" && !isCarouselKind ? (
          <FilterChipGroup
            label="Game stage"
            value={gameStageFilter}
            onChange={setGameStageFilter}
            options={[
              { value: "all", label: "All" },
              ...gameStages,
            ]}
          />
        ) : null}
        {isCandidateAttackKind ? (
          <>
            <FilterChipGroup
              label="Before contact"
              value={incomingBounceFilter}
              onChange={setIncomingBounceFilter}
              options={[
                { value: "all", label: "All" },
                ...incomingBounceKinds.map((kind) => ({
                  value: kind,
                  label: incomingBounceLabel(kind as CoachViewMoment["incomingBounceKind"]),
                })),
              ]}
            />
            <FilterChipGroup
              label="Estimated direction"
              value={speedupLaneFilter}
              onChange={setSpeedupLaneFilter}
              options={[
                { value: "all", label: "All" },
                ...speedupLanes.map((kind) => ({
                  value: kind,
                  label: speedupLaneLabel(kind as CoachViewMoment["speedupLane"]),
                })),
              ]}
            />
          </>
        ) : null}
        {props.kind === "leftSideCoverage" ? (
          <>
            <FilterChipGroup
              label="Took it?"
              value={coverageOutcomeFilter}
              onChange={setCoverageOutcomeFilter}
              options={[
                { value: "all", label: "All" },
                ...coverageOutcomes.map((outcome) => ({
                  value: outcome,
                  label: coverageOutcomeLabel(
                    outcome as CoachViewMoment["coverageOutcome"],
                  ),
                })),
              ]}
            />
          </>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setReviewedPlayerFilter("all");
            setHitterFilter("all");
            setHitterTeamFilter("all");
            setWinnerFilter("all");
            setShotTypeFilter(defaultShotTypeFilter);
            setGameStageFilter(defaultGameStageFilter);
            setPartnerFilter("all");
            setCoverageKindFilter("all");
            setCoverageOutcomeFilter("all");
            setIncomingBounceFilter("all");
            setSpeedupLaneFilter("all");
            setPartnerSideSort("none");
          }}
          sx={{ textTransform: "none", fontWeight: 900, height: 30 }}
        >
          Clear filters
        </Button>
        {isCarouselKind && showPaddleMoreFilters ? (
          <Box
            sx={{
              width: "100%",
              pt: 0.85,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "end",
            }}
          >
            {props.kind !== "candidateSpeedup" ? (
              <>
                <PlayerFilterChipGroup
                  label="Reviewed player"
                  value={reviewedPlayerFilter}
                  onChange={setReviewedPlayerFilter}
                  playerIds={playerIds}
                  renderPlayerIdentity={renderPlayerIdentity}
                  insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                  avatarSize={32}
                  buttonHeight={32}
                  fontSize={12}
                />
                <PlayerFilterChipGroup
                  label="Hitter"
                  value={hitterFilter}
                  onChange={handleHitterFilterChange}
                  playerIds={playerIds}
                  renderPlayerIdentity={renderPlayerIdentity}
                  insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                  avatarSize={32}
                  buttonHeight={32}
                  fontSize={12}
                />
              </>
            ) : null}
            <FilterChipGroup
              label="Hitter team"
              value={hitterTeamFilter}
              onChange={setHitterTeamFilter}
              options={[
                { value: "all", label: "All" },
                { value: "0", label: teamOption(0) },
                { value: "1", label: teamOption(1) },
              ]}
            />
            <FilterChipGroup
              label="Rally winner"
              value={winnerFilter}
              onChange={setWinnerFilter}
              options={[
                { value: "all", label: "All" },
                { value: "0", label: teamOption(0) },
                { value: "1", label: teamOption(1) },
              ]}
            />
            {props.kind !== "candidateSpeedup" ? (
              <>
                <FilterChipGroup
                  label="Shot type"
                  value={shotTypeFilter}
                  onChange={setShotTypeFilter}
                  options={[
                    { value: "all", label: "All" },
                    ...shotTypes.map((type) => ({ value: type, label: shotTypeLabel(type) })),
                  ]}
                />
                <FilterChipGroup
                  label="Game stage"
                  value={gameStageFilter}
                  onChange={setGameStageFilter}
                  options={[
                    { value: "all", label: "All" },
                    ...gameStages,
                  ]}
                />
              </>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {props.kind === "leftSideCoverage" ? (
        <Box
          sx={{
            px: 1.25,
            py: 0.7,
            display: "flex",
            gap: 0.75,
            alignItems: "stretch",
            flexWrap: "wrap",
            borderBottom: "1px solid rgba(0,0,0,0.10)",
            bgcolor: "rgba(25,118,210,0.035)",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 950,
              color: "rgba(0,0,0,0.62)",
              alignSelf: "center",
            }}
          >
            Partner-side distance from center line
          </Typography>
          {playerIds.map((playerId) => {
            const stat = leftSidePlayerReachStats.get(playerId);
            const avgTook =
              stat && stat.tookDistanceCount > 0
                ? stat.tookDistanceSum / stat.tookDistanceCount
                : null;
            const maxBarPct = Math.min(
              100,
              ((stat?.maxCandidateDistanceFt ?? 0) / 6) * 100,
            );
            return (
              <Box
                key={playerId}
                sx={{
                  minWidth: 178,
                  px: 0.75,
                  py: 0.55,
                  border: "1px solid rgba(25,118,210,0.25)",
                  borderRadius: 1.25,
                  bgcolor: "rgba(255,255,255,0.78)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.55 }}>
                  <PlayerMini
                    playerId={playerId}
                    renderPlayerIdentity={renderPlayerIdentity}
                    insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                    size={26}
                  />
                  <Typography sx={{ fontSize: 11, fontWeight: 950 }}>
                    {stat ? `${stat.took}/${stat.total} took` : "0/0 took"}
                  </Typography>
                </Box>
                <Box sx={{ mt: 0.45, display: "flex", gap: 0.8 }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 850 }}>
                    took max {formatFeet(stat?.maxTookDistanceFt)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "rgba(0,0,0,0.62)" }}>
                    avg {formatFeet(avgTook)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    mt: 0.45,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: "rgba(25,118,210,0.12)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${maxBarPct}%`,
                      height: "100%",
                      bgcolor: "#1976d2",
                    }}
                  />
                </Box>
                <Typography sx={{ mt: 0.25, fontSize: 10, color: "rgba(0,0,0,0.58)" }}>
                  seen up to {formatFeet(stat?.maxCandidateDistanceFt)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setReviewedPlayerFilter(String(playerId))}
                  sx={{
                    mt: 0.45,
                    height: 22,
                    px: 0.75,
                    py: 0,
                    textTransform: "none",
                    fontSize: 10.5,
                    fontWeight: 950,
                  }}
                >
                  Show player
                </Button>
              </Box>
            );
          })}
        </Box>
      ) : null}

      {props.kind === "leftSideCoverage" ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "rgba(5,8,12,0.96)",
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.75,
              borderBottom: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <Button
              size="small"
              variant={
                coverageMainSortMode === "partnerDistanceDesc"
                  ? "contained"
                  : "outlined"
              }
              onClick={() => setCoverageMainSortMode("partnerDistanceDesc")}
              sx={{ textTransform: "none", fontWeight: 950 }}
            >
              Sort by Partner ft
            </Button>
            <Button
              size="small"
              variant={
                coverageMainSortMode === "rallyAsc" ||
                coverageMainSortMode === "rallyDesc"
                  ? "contained"
                  : "outlined"
              }
              onClick={() =>
                setCoverageMainSortMode((current) =>
                  current === "rallyAsc" ? "rallyDesc" : "rallyAsc",
                )
              }
              sx={{ textTransform: "none", fontWeight: 950 }}
            >
              Sort by Rally
              {coverageMainSortMode === "rallyAsc"
                ? " ↑"
                : coverageMainSortMode === "rallyDesc"
                  ? " ↓"
                  : ""}
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              p: 1,
              pt: 0.8,
              display: "flex",
              gap: 1,
            }}
          >
            <CoverageOutcomeReviewPane
              title="Took it"
              emptyText="No took-it moments with the current filters."
              moments={coverageTookMoments}
              allMoments={moments}
              rows={rows}
              packageId={packageId}
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
              renderPlayerIdentity={renderPlayerIdentity}
              firefightsClips={firefightsClips}
              firefightRallySet={firefightRallySet}
              activeColor="rgba(46,125,50,0.86)"
              dense={coverageTookMoments.length > 12}
              playerId={coverageCarouselPlayerId}
            />
            <Box
              aria-hidden="true"
              sx={{
                flex: "0 0 4px",
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.22)",
                boxShadow: "0 0 20px rgba(128,216,255,0.22)",
              }}
            />
            <CoverageOutcomeReviewPane
              title="Did not take it"
              emptyText="No did-not-take moments with the current filters."
              moments={coverageDidNotTakeMoments}
              allMoments={moments}
              rows={rows}
              packageId={packageId}
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
              renderPlayerIdentity={renderPlayerIdentity}
              firefightsClips={firefightsClips}
              firefightRallySet={firefightRallySet}
              activeColor="rgba(25,118,210,0.86)"
              dense={coverageDidNotTakeMoments.length > 12}
              playerId={coverageCarouselPlayerId}
            />
          </Box>
        </Box>
      ) : isCarouselKind ? (
        <PaddleReadinessCarousel
          kind={props.kind}
          moments={sortedFiltered}
          allMoments={moments}
          emptyMessage={
            props.kind === "candidateSpeedup"
              ? "No possible speedup review moments with the current filters."
              : props.kind === "candidateBodybag"
              ? "No possible jammed/body-contact review moments with the current filters."
              : props.kind === "possiblePopup"
              ? "No possible popup moments with the current player and court-state filters."
              : props.kind === "dinkReadiness"
              ? "No dink focus moments with the current filters."
              : undefined
          }
          previewScale={paddlePreviewScale}
          packageId={packageId}
          rows={rows}
          insightsToCvPlayerIndex={insightsToCvPlayerIndex}
          renderPlayerIdentity={renderPlayerIdentity}
          firefightsClips={firefightsClips}
          firefightRallySet={firefightRallySet}
          cvJson={props.cvJson}
        />
      ) : (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box
          sx={{
            width: tablePanelWidth,
            minWidth: "min(62vw, 680px)",
            maxWidth: "64vw",
            overflow: "auto",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            "& .MuiTableCell-root": {
              px: 0.75,
              py: 0.65,
              verticalAlign: "top",
            },
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: tableMinWidth, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 900, width: 54 }}>Rally</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 66 }}>Shot</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 86 }}>Winner</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 150 }}>Stage of Rally</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 92 }}>Hitter</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 105 }}>
                  Reviewed
                </TableCell>
                <TableCell sx={{ fontWeight: 900, width: 260 }}>
                  Reason
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFiltered.map((moment) => {
                const isSelected = moment.key === selected?.key;
                return (
                  <TableRow
                    key={moment.key}
                    hover
                    selected={isSelected}
                    onClick={() => setSelectedKey(moment.key)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {moment.rallyIndex + 1}
                      {moment.score ? <Box sx={{ fontSize: 11, opacity: 0.6 }}>{moment.score}</Box> : null}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {moment.shotNumber}
                      <Box sx={{ fontSize: 11, opacity: 0.62 }}>
                        {shotTypeLabel(moment.shotType)}
                      </Box>
                      {isCandidateAttackKind ? (
                        <Box sx={{ fontSize: 11, opacity: 0.62 }}>
                          {speedupLaneLabel(moment.speedupLane)} ·{" "}
                          {incomingBounceLabel(moment.incomingBounceKind)}
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 850 }}>
                        {moment.rallyWinnerTeamId === 0 || moment.rallyWinnerTeamId === 1
                          ? teamOption(moment.rallyWinnerTeamId)
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontWeight: 850 }}>
                        {moment.gameStageLabel}
                      </Typography>
                      <Box sx={{ fontSize: 11, opacity: 0.62 }}>
                        Hitter {zoneLabel(moment.hitterPlayerZone)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <PlayerMini
                        playerId={moment.hitterPlayerId}
                        renderPlayerIdentity={renderPlayerIdentity}
                        insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                      />
                    </TableCell>
                    <TableCell>
                      <PlayerMini
                        playerId={moment.reviewedPlayerId}
                        renderPlayerIdentity={renderPlayerIdentity}
                        insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                        highlighted
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                        {moment.candidateReason ?? "Opponent contact frame"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", bgcolor: "#000" }}>
          <Box sx={{ px: 1.25, py: 0.9, bgcolor: "#fff", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            {selected ? (
              <Typography sx={{ fontWeight: 950 }}>
                Rally {selected.rallyIndex + 1} · Shot {selected.shotNumber} · inspect{" "}
                {props.playerLabel?.(selected.reviewedPlayerId) ?? `player ${selected.reviewedPlayerId}`}
                {" "}at opponent contact
              </Typography>
            ) : (
              <Typography sx={{ opacity: 0.7 }}>No matching moments.</Typography>
            )}
          </Box>

          {selected && selectedRow ? (
            <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
              <ActiveRallyVideo
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                renderPlayerIdentity={renderPlayerIdentity}
                aiContextLabel={props.title}
                focusedFilter={null}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                activeShotNumber={selected.shotNumber}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls
                showTitle={false}
                showScoreEtc={false}
                showAi
                aiDrawerMode="split"
                showLoopScrubber
                defaultLoopUiOpen={false}
                showPopoutButton
                isInFullPopup={false}
                rallyLensPanelPlacement="embeddedBottom"
                focusedPlayerId={selected.reviewedPlayerId}
                rallyLensMoments={selectedRowLensMoments}
                initialRallyLens={selectedRowInitialLens}
                initialRallyLensMomentKey={activeRallyLensKeyForCoachMoment(selected)}
                showRallyLensControls={false}
                seedPlayback={{
                  timeSec: seedTimeSec,
                  paused: true,
                  nonce: seedNonce,
                }}
                seedLoopRegion={selectedLoopRegion}
                seekToActiveShotOnChange={false}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: 12,
                  top: 4,
                  px: 1,
                  py: 0.45,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.70)",
                  color: "#fff",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  maxWidth: "calc(100% - 24px)",
                }}
              >
                <Box sx={{ fontSize: 11, fontWeight: 950, whiteSpace: "nowrap" }}>
                  Opponent contact frame
                </Box>
                <Box
                  sx={{
                    fontSize: 11,
                    opacity: 0.82,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Hitter: {props.playerLabel?.(selected.hitterPlayerId) ?? `player ${selected.hitterPlayerId}`} ·
                  {" Reviewed: "}
                  {props.playerLabel?.(selected.reviewedPlayerId) ?? `player ${selected.reviewedPlayerId}`}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2, color: "rgba(255,255,255,0.75)" }}>No video selected.</Box>
          )}
        </Box>
      </Box>
      )}
    </Paper>
    <CoveragePlayerBroadReviewPopup
      open={props.kind === "leftSideCoverage" && coverageBroadReviewPlayerId != null}
      playerId={coverageBroadReviewPlayerId}
      moments={coverageBroadReviewMoments}
      allMoments={moments}
      onClose={() => setCoverageBroadReviewPlayerId(null)}
      packageId={packageId}
      rows={rows}
      insightsToCvPlayerIndex={insightsToCvPlayerIndex}
      renderPlayerIdentity={renderPlayerIdentity}
      firefightsClips={firefightsClips}
      firefightRallySet={firefightRallySet}
    />
    <PaddlePlayerBroadReviewPopup
      open={isReadinessKind && paddleBroadReviewPlayerId != null}
      kind={props.kind}
      title={
        props.kind === "dinkReadiness"
          ? "Dink focus broad review"
          : "Paddle readiness broad review"
      }
      playerId={paddleBroadReviewPlayerId}
      moments={paddlePlayerReviewMoments}
      allMoments={moments}
      emptyMessage={
        props.kind === "dinkReadiness"
          ? "No dink focus moments for this player with the current filters."
          : undefined
      }
      onClose={() => setPaddleBroadReviewPlayerId(null)}
      packageId={packageId}
      rows={rows}
      insightsToCvPlayerIndex={insightsToCvPlayerIndex}
      renderPlayerIdentity={renderPlayerIdentity}
      firefightsClips={firefightsClips}
      firefightRallySet={firefightRallySet}
    />
    </>
  );
}

export function CandidateSpeedupsPopup(props: Omit<React.ComponentProps<typeof CoachMomentGalleryPopup>, "title" | "subtitle" | "kind" | "icon">) {
  return (
    <CoachMomentGalleryPopup
      {...props}
      title="Candidate Speedup Opportunities"
      subtitle="Experimental kitchen-exchange review moments that match possible speedup shape. These are evidence clips, not confirmed intentional speedups. Excludes 3rds, deep/baseline shots, ATPs, dinks, and drops; kept when the next opponent touch is a volley or the shot ends the point. Direction is estimated."
      kind="candidateSpeedup"
      icon={<FlashOnIcon sx={{ color: "#1976d2" }} />}
    />
  );
}

export function CandidateBodybagsPopup(props: Omit<React.ComponentProps<typeof CoachMomentGalleryPopup>, "title" | "subtitle" | "kind" | "icon">) {
  return (
    <CoachMomentGalleryPopup
      {...props}
      title="Jammed / Bodybag Candidates"
      subtitle="Experimental review moments for possible body-contact, jammed-player, or unprepared-contact shots. Uses explicit PB Vision/text labels when present; otherwise looks for speed, an estimated ball endpoint near an opponent, and a plausible follow-up or point-ending shape. These are candidate clips only, not confirmed bodybags."
      kind="candidateBodybag"
      icon={<FlashOnIcon sx={{ color: "#8e24aa" }} />}
    />
  );
}

export function PossiblePopupsPopup(props: Omit<React.ComponentProps<typeof CoachMomentGalleryPopup>, "title" | "subtitle" | "kind" | "icon">) {
  return (
    <CoachMomentGalleryPopup
      {...props}
      title="Possible Popups"
      subtitle="Review dinks, drops, resets, blocks, and defensive replies that may have sat up. Candidates use measured height over the net, peak height, speed, and post-net rise; these are clips to inspect, not confirmed errors."
      kind="possiblePopup"
      icon={<FlashOnIcon sx={{ color: "#ef6c00" }} />}
    />
  );
}

export function PaddleReadinessReviewPopup(props: Omit<React.ComponentProps<typeof CoachMomentGalleryPopup>, "title" | "subtitle" | "kind" | "icon">) {
  return (
    <CoachMomentGalleryPopup
      {...props}
      title="Paddle Position / Readiness Review"
      subtitle=""
      kind="paddleReadiness"
      icon={<PaddleReadinessIcon size={24} />}
    />
  );
}

export function DinkReadinessReviewPopup(props: Omit<React.ComponentProps<typeof CoachMomentGalleryPopup>, "title" | "subtitle" | "kind" | "icon">) {
  return (
    <CoachMomentGalleryPopup
      {...props}
      title="Dink Focus"
      subtitle=""
      kind="dinkReadiness"
      icon={<PaddleReadinessIcon size={24} />}
    />
  );
}

export function LeftSideCoverageReviewPopup(props: Omit<React.ComponentProps<typeof CoachMomentGalleryPopup>, "title" | "subtitle" | "kind" | "icon">) {
  return (
    <CoachMomentGalleryPopup
      {...props}
      title="Left-Side Coverage Review"
      subtitle=""
      kind="leftSideCoverage"
      icon={<LeftRightCoverageIcon size={22} />}
    />
  );
}
